import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useEmpresa } from '../contexts/EmpresaContext'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import NaturezaSelect from '../components/NaturezaSelect'
import ContaSelect from '../components/ContaSelect'
import ItemSelect from '../components/ItemSelect'
import CentroCustoSelect from '../components/CentroCustoSelect'
import ServicoPrestadoSelect from '../components/ServicoPrestadoSelect'
import ModalRecapagemPneu from '../components/ModalRecapagemPneu'
import { useIbsCbsDescricoes } from '../hooks/useIbsCbsDescricoes'
import { AbasIbsCbs, TabelaIbsCbsMono, IbsCbsMonoLinha } from '../components/IbsCbsMono'
import { registrarGuardaSaida } from '../utils/guardaSaida'
import { avisosPeriodo, PeriodoCompras } from '../utils/periodoCompras'

// Comprador desativado não aparece na busca do lançamento
const SOMENTE_ATIVOS = { ativos: '1' }

interface Natureza {
  tno_cod: number
  tno_cdf: number
  tno_des: string
  tno_dsc: string
  tno_loc?: string
  // 'S' = essa Natureza integra com Contas a Pagar — nesse caso o Financeiro
  // da nota deixa de ser opcional (mesma regra do PIMPNFC legado: Integra_Financeiro).
  tno_ctp?: string
}

function fmtCnpj(doc: string) {
  const n = (doc || '').replace(/\D/g, '')
  if (n.length !== 14) return doc || ''
  return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
}
function hojeISO() { return new Date().toISOString().slice(0, 10) }
// Hora atual em HH.MM (mesmo formato decimal já usado em pnv_hor/ordhrt) — hora
// do lançamento da nota, não um horário fixo.
function horaAtualStr() {
  const agora = new Date()
  return `${String(agora.getHours()).padStart(2, '0')}.${String(agora.getMinutes()).padStart(2, '0')}`
}
function diasAtrasISO(dias: number) {
  const d = new Date()
  d.setDate(d.getDate() - dias)
  return d.toISOString().slice(0, 10)
}
function parseNumBr(v: string) {
  const s = String(v ?? '').trim()
  if (s.includes(',')) return Number(s.replace(/\./g, '').replace(',', '.')) || 0
  return Number(s) || 0
}
function fmtQtd(v: string) { return parseNumBr(v).toLocaleString('pt-BR', { minimumFractionDigits: 3 }) }
function fmtMoeda(v: string) { return parseNumBr(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) }
function fmtUnit(v: number) { return v.toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 }) }
// vUnCom do XML quando veio da NF-e; senão Val. Total ÷ Quantidade
function valorUnitarioItem(f: { valorUnitario: string; valorTotal: string; quantidade: string }) {
  if (f.valorUnitario) return parseNumBr(f.valorUnitario)
  const q = parseNumBr(f.quantidade)
  return q > 0 ? parseNumBr(f.valorTotal) / q : 0
}
function round2(v: number) { return Math.round((v + Number.EPSILON) * 100) / 100 }
function somarDiasISO(dataISO: string, dias: number) {
  const d = new Date(dataISO + 'T00:00:00')
  d.setDate(d.getDate() + dias)
  return d.toISOString().slice(0, 10)
}
function fmtDataBr(iso: string) { return iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '-' }
// input type="date" aceita ano com mais de 4 dígitos (o navegador deixa continuar digitando
// no campo do ano sem limite) — ignora a mudança se o ano digitado passar de 4 dígitos.
function limitarAno4Digitos(valorAtual: string, valorNovo: string) {
  const ano = valorNovo.split('-')[0]
  return ano && ano.length > 4 ? valorAtual : valorNovo
}

function Secao({ titulo, colapsavel, children }: { titulo: string; colapsavel?: boolean; children: React.ReactNode }) {
  const [aberta, setAberta] = useState(!colapsavel)
  return (
    <div style={{
      background: '#f7faff', border: '1px solid #eef0f4', borderLeft: '4px solid #1a6ea8',
      borderRadius: '0.5rem', padding: '0.7rem 0.9rem', marginBottom: '0.7rem',
    }}>
      <h4
        onClick={colapsavel ? () => setAberta(a => !a) : undefined}
        style={{
          fontSize: '0.68rem', fontWeight: 700, color: '#1a6ea8', textTransform: 'uppercase', letterSpacing: '0.05em',
          marginBottom: aberta ? '0.5rem' : 0, display: 'flex', alignItems: 'center', gap: '0.3rem',
          cursor: colapsavel ? 'pointer' : undefined, userSelect: colapsavel ? 'none' : undefined,
        }}
      >
        {colapsavel && <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: aberta ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>}
        {titulo}
      </h4>
      {aberta && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.8rem', alignItems: 'flex-end' }}>
          {children}
        </div>
      )}
    </div>
  )
}

// Divide visualmente os campos dentro de uma Secao em subgrupos (ex: Veículo, Pneu,
// Abastecimento) — evita que uma lista longa de campos condicionais pareça uma
// bagunça só porque tecnicamente cabem na mesma seção.
// `destaque` é pra subtítulos que merecem mais peso que os demais (ex: "Impostos"
// dentro de Complemento/Impostos) — usa a cor/força do título da própria Secao.
function Divisor({ titulo, destaque }: { titulo: string; destaque?: boolean }) {
  return (
    <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.6rem', margin: destaque ? '0.5rem 0 0.15rem' : '0.35rem 0 0.05rem' }}>
      <span style={{
        fontSize: destaque ? '0.72rem' : '0.62rem', fontWeight: 700, color: destaque ? '#1a6ea8' : '#8a97a8',
        textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
      }}>
        {titulo}
      </span>
      <span style={{ flex: 1, height: destaque ? 2 : 1, background: destaque ? '#dbeafe' : '#e2e8f0', borderRadius: 1 }} />
    </div>
  )
}

// Tabela compacta de impostos (uma linha por tributo) — mesmo estilo enxuto e sóbrio
// da tela de impostos do CT-e OS (cinza/preto, sem cor por linha), só que com campos
// editáveis em vez de texto fixo. O `height` explícito é pra vencer a regra global
// `table input { height: 34px }` do index.css, feita pra tabelas com campo maior.
type CampoNum = { value: string; onChange: (v: string) => void }

type CorDestaque = { bg: string; borda: string; texto: string }
const DESTAQUE_XML: CorDestaque = { bg: '#eff6ff', borda: '#93c5fd', texto: '#1d4ed8' }
// Amarelo/âmbar — usado só nos campos de PIS/COFINS "de referência" (o que o
// fornecedor declarou no XML), pra não confundir visualmente com os campos azuis
// (que são os que realmente valem pro cálculo fiscal, vindos da CFOP/Natureza).
const DESTAQUE_XML_REF: CorDestaque = { bg: '#fffbeb', borda: '#fbbf24', texto: '#92400e' }

function CampoTabela({ value, onChange, mono, len, readOnly, titulo, cor }: CampoNum & { mono?: boolean; len?: number; readOnly?: boolean; titulo?: string; cor?: CorDestaque }) {
  const tam = len ?? (mono ? 3 : undefined)
  const c = cor ?? DESTAQUE_XML
  return (
    <input
      type="text" inputMode={mono ? undefined : 'decimal'} value={value} readOnly={readOnly} title={titulo}
      onChange={e => { if (!readOnly) onChange(tam ? e.target.value.slice(0, tam) : e.target.value) }}
      style={{
        width: '100%', height: 24, padding: '0 5px', fontSize: '0.76rem', borderRadius: 3,
        textAlign: mono ? 'center' : 'right', fontFamily: mono ? 'monospace' : undefined, boxSizing: 'border-box',
        border: readOnly ? `1px solid ${c.borda}` : '1px solid #e2e8f0',
        background: readOnly ? c.bg : undefined,
        color: readOnly ? c.texto : undefined,
        fontWeight: readOnly ? 700 : undefined,
        cursor: readOnly ? 'not-allowed' : undefined,
      }}
    />
  )
}

function LinhaImposto({ nome, base, perc, valor, cst, travado, cor, cstFixo = true, motivo }: {
  nome: string; base?: CampoNum; perc?: CampoNum; valor?: CampoNum; cst?: CampoNum; travado?: boolean; cor?: CorDestaque
  // cstFixo=true (default): CST sempre travado, vem da CFOP (linhas de ICMS/IPI/PIS/Cofins
  // "oficiais"). cstFixo=false: CST só trava junto com o resto quando `travado`
  // (linhas de referência do XML, tipo PIS/COFINS (XML)).
  cstFixo?: boolean
  // Texto do tooltip quando travado — default é "veio do XML"; PIS/Cofins oficiais
  // usam motivo="cfop" (travados sempre, cálculo vem da CFOP, não do XML).
  motivo?: 'xml' | 'cfop'
}) {
  const td: React.CSSProperties = { padding: '2px 5px', border: '1px solid #eef0f4' }
  const vazio = <span style={{ color: '#d1d5db' }}>—</span>
  const tit = travado ? (motivo === 'cfop' ? 'Cálculo vem da CFOP — não é editável aqui' : 'Veio do XML importado — não editável') : undefined
  const cstTravado = cstFixo ? true : travado
  const cstTit = cstFixo ? 'CST vem da CFOP, não é editável aqui' : tit
  return (
    <tr>
      <td style={{ ...td, fontWeight: 700, whiteSpace: 'nowrap' }}>{nome}</td>
      <td style={td}>{base ? <CampoTabela {...base} readOnly={travado} titulo={tit} cor={cor} /> : vazio}</td>
      <td style={td}>{perc ? <CampoTabela {...perc} readOnly={travado} titulo={tit} cor={cor} /> : vazio}</td>
      <td style={td}>{valor ? <CampoTabela {...valor} readOnly={travado} titulo={tit} cor={cor} /> : vazio}</td>
      <td style={{ ...td, width: 55 }}>{cst ? <CampoTabela {...cst} mono readOnly={cstTravado} titulo={cstTit} cor={cor} /> : vazio}</td>
    </tr>
  )
}

function LinhaIbsCbs({ nome, base, bcEf, aliRef, redAli, aliEf, valor, travado }: {
  nome: string; base?: CampoNum; bcEf?: CampoNum; aliRef?: CampoNum; redAli?: CampoNum; aliEf?: CampoNum; valor?: CampoNum; travado?: boolean
}) {
  const td: React.CSSProperties = { padding: '2px 5px', border: '1px solid #eef0f4' }
  const vazio = <span style={{ color: '#d1d5db' }}>—</span>
  const tit = travado ? 'Veio do XML importado — não editável' : undefined
  return (
    <tr>
      <td style={{ ...td, fontWeight: 700, whiteSpace: 'nowrap' }}>{nome}</td>
      <td style={td}>{base ? <CampoTabela {...base} readOnly={travado} titulo={tit} /> : vazio}</td>
      <td style={td}>{bcEf ? <CampoTabela {...bcEf} readOnly={travado} titulo={tit} /> : vazio}</td>
      <td style={td}>{aliRef ? <CampoTabela {...aliRef} readOnly={travado} titulo={tit} /> : vazio}</td>
      <td style={td}>{redAli ? <CampoTabela {...redAli} readOnly={travado} titulo={tit} /> : vazio}</td>
      <td style={td}>{aliEf ? <CampoTabela {...aliEf} readOnly={travado} titulo={tit} /> : vazio}</td>
      <td style={td}>{valor ? <CampoTabela {...valor} readOnly={travado} titulo={tit} /> : vazio}</td>
    </tr>
  )
}

const ESTILO_TOTAL: React.CSSProperties = { background: '#fffbeb', borderColor: '#fcd34d', color: '#92400e', fontWeight: 600 }

// Destaque pros botões de ação do rodapé (Novo Item/Financeiro/Concluir) — um
// pouco maiores e com sombra, pra não se confundir com botões secundários comuns.
const BTN_DESTAQUE: React.CSSProperties = {
  fontWeight: 700, padding: '0.5rem 1rem', boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
}

const ITEM_VAZIO = {
  linha: 0 as number | null,
  ordemCompraNum: '', ordemCompraLinha: '',
  itemCod: '', itemDes: '', itemUn: '', itemTipo: 'P',
  descComplementar: '',
  quantidade: '0,000', valorTotal: '0,00',
  valorUnitario: '', // vUnCom do XML (vazio = Val. Total ÷ Quantidade)
  naturezaCod: '', cfopCod: '', cfopDes: '',
  origemCod: 'FOR', origemDes: 'FORNECEDOR',
  destinoCod: '', destinoDes: '', destinoTipo: '' as number | '',
  // oed_vei='S' no cadastro do destino ("informa veículo sempre") — troca o
  // gatilho fixo de destinoCod==='VEI' por uma regra de verdade (qualquer
  // destino pode exigir veículo, não só um código fixo).
  destinoVeiSempre: false,
  veiculoCod: '', veiculoDes: '',
  hodometro: '', hora: '12.00',
  pneuCod: '', pneuDes: '', recapagem: 'N',
  postoCod: '', postoDes: '',
  tipoCombustivel: '',
  osAutomatica: 'N', ordemServicoCod: '', ordemServicoDes: '', ordemServicoLinha: '',
  // true quando a OS vinculada/incluída já está executada (ordexe='E') — nesse
  // caso não repete a pergunta de setor/data de finalização.
  osExecutada: false,
  tipoServicoCod: '', tipoServicoDes: '',
  aplicacaoCod: '', aplicacaoDes: '',
  contaEstoqueCod: '', contaEstoqueDes: '',
  centroCustoCod: '', centroCustoDes: '',
  rateioCusto: '', numeroParcelas: '',
  // Rateio de verdade (Rateio do Custo = "C") — mais de uma Conta Aplicação/
  // Centro de Custo pro mesmo item, grava em frota.ntvccu (WNTVCCU no Win).
  rateioLinhas: [] as { centroCustoCod: string; centroCustoDes: string; aplicacaoCod: string; aplicacaoDes: string; percentual: string; valor: string }[],
  // Complemento/Impostos
  ncmCod: '', ncmDes: '',
  nbsCod: '', nbsDes: '',
  servicoCod: '', servicoDes: '',
  itemValorFrete: '0,00', itemValorAcrescimo: '0,00',
  itemValorSeguro: '0,00', itemValorDesconto: '0,00',
  valorContabil: '0,00',
  inssBase: '0,00', inssPerc: '0,00', inssValor: '0,00',
  icmsBase: '0,00', icmsPerc: '0,00', icmsValor: '0,00', icmsCst: '',
  icmsStBase: '0,00', icmsStPerc: '0,00', icmsStValor: '0,00',
  ipiBase: '0,00', ipiPerc: '0,00', ipiValor: '0,00', ipiCst: '',
  ipiXmlBase: '0,00', ipiXmlPerc: '0,00', ipiXmlValor: '0,00', // IPI declarado no XML — o cálculo da Natureza não sobrescreve
  difal: '0,00',
  pisBase: '0,00', pisPerc: '0,00', pisValor: '0,00', pisCst: '',
  cofinsBase: '0,00', cofinsPerc: '0,00', cofinsValor: '0,00', cofinsCst: '',
  // PIS/COFINS declarado pelo fornecedor no XML — só referência (o cálculo oficial
  // é o pisBase/cofinsBase acima, sempre da CFOP/Natureza). Preenchido só na importação.
  pisXmlBase: '0,00', pisXmlAliq: '0,00', pisXmlValor: '0,00', pisXmlCst: '',
  cofinsXmlBase: '0,00', cofinsXmlAliq: '0,00', cofinsXmlValor: '0,00', cofinsXmlCst: '',
  // IBS/CBS (Reforma Tributária) — vem do XML da nota (quando disponível) ou é
  // digitado na mão; não passa pelo motor de cálculo (é o valor que o
  // fornecedor apurou, não algo que recalculamos com regra nossa)
  ibsCbsCst: '', ibsCbsClassTrib: '',
  cbsBase: '0,00', cbsBcEf: '0,00', cbsAliRef: '0,00', cbsRedAli: '0,00', cbsAliEf: '0,00', cbsValor: '0,00',
  ibsUfEst: '', ibsUfBcEf: '0,00', ibsUfAliRef: '0,00', ibsUfRedAli: '0,00', ibsUfAliEf: '0,00', ibsUfValor: '0,00',
  ibsMunBase: '0,00', ibsMunBcEf: '0,00', ibsMunAliRef: '0,00', ibsMunRedAli: '0,00', ibsMunAliEf: '0,00', ibsMunValor: '0,00',
  isBase: '0,00', isAliq: '0,00',
  // IBS/CBS monofásico (combustível), só leitura — vem do XML
  ibsCbsMono: [] as IbsCbsMonoLinha[],
}
type ItemNota = typeof ITEM_VAZIO
// Valores (R$) do IBS/CBS que se dividem entre os itens de uma NFS-e importada; alíquotas ficam iguais
const CAMPOS_RATEIO_NFSE = [
  'cbsBase', 'cbsBcEf', 'cbsValor', 'ibsUfBcEf', 'ibsUfValor', 'ibsMunBase', 'ibsMunBcEf', 'ibsMunValor', 'isBase',
  'pisXmlBase', 'pisXmlValor', 'cofinsXmlBase', 'cofinsXmlValor',
] as const

function Campo({ label, width, children }: { label: string; width?: number | string; children: React.ReactNode }) {
  return (
    <div className="field-cad" style={{ width, gap: '0.15rem' }}>
      <label style={{ fontSize: '0.66rem' }}>{label}</label>
      {children}
    </div>
  )
}

// modeloFixo="99": tela "Lançamento NFS-e 99" (mesma tela, presa no modelo 99).
const ROTA_LANCAMENTO_NFSE = '/compras/recebimentos/lancamento-nfse99'

export default function LancamentoNotas({ modeloFixo }: { modeloFixo?: '99' } = {}) {
  const { empresas, empresaCod, setEmpresaCod, empresaAtual } = useEmpresa()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const importarChaveUrl = searchParams.get('importarChave') || ''

  // Cabeçalho
  const [serie, setSerie] = useState('')
  const [numeroNota, setNumeroNota] = useState('')
  const [fornecedorCod, setFornecedorCod] = useState('')
  const [fornecedorDes, setFornecedorDes] = useState('')
  const [fornecedorBloqueado, setFornecedorBloqueado] = useState(false)
  const [fornecedorFj, setFornecedorFj] = useState('')
  const [fornecedorUf, setFornecedorUf] = useState('')
  const [fornecedorCidade, setFornecedorCidade] = useState('')
  const [fornecedorCnpj, setFornecedorCnpj] = useState('')
  const [avisoChaveCnpj, setAvisoChaveCnpj] = useState('')

  const [naturezas, setNaturezas] = useState<Natureza[]>([])

  const { cstDes, classTribDes } = useIbsCbsDescricoes()

  const [salvando, setSalvando] = useState(false)
  const [erroSalvar, setErroSalvar] = useState('')
  const [notaSalva, setNotaSalva] = useState<{ empcod: number; serie: string; numero: number } | null>(null)

  async function onSelecionarFornecedor(cod: string, des: string, item: any) {
    setFornecedorCod(cod); setFornecedorDes(des)
    setFornecedorBloqueado(!!item?.bloqueado)
    setAvisoOrdensVinculadas('')
    setFornecedorFj(item?.cli_fj || '')
    setFornecedorUf(item?.end_uf || '')
    setFornecedorCidade(item?.end_cidade || '')
    setFornecedorCnpj(item?.cli_mat || '')
    try {
      const r = await api.get(`/erp/clientes/${cod}/adicionais`)
      const ad = r.data || {}
      if (ad.cmp_nat && !naturezaOpCod1) {
        setNaturezaOpCod1(String(ad.cmp_nat))
        setNaturezaOpCod2(String(ad.cmp_nat_cdf || ''))
        setNaturezaOpDes(ad.cmp_nat_des || '')
      }
      if (ad.cmp_conta && !contaFinanceira) {
        setContaFinanceira(ad.cmp_conta)
        setContaFinanceiraDes(ad.cmp_conta_des || '')
      }
    } catch { /* sugestão de natureza/conta é best-effort, não bloqueia a digitação */ }
  }

  const [ordensVinculadas, setOrdensVinculadas] = useState<any[] | null>(null)
  const [carregandoVinculadas, setCarregandoVinculadas] = useState(false)
  const [modalVinculadasAberto, setModalVinculadasAberto] = useState(false)
  const [avisoOrdensVinculadas, setAvisoOrdensVinculadas] = useState('')

  async function abrirOrdensVinculadas() {
    if (!fornecedorCod) { setAvisoOrdensVinculadas('Escolha o fornecedor primeiro'); return }
    setAvisoOrdensVinculadas('')
    setModalVinculadasAberto(true); setCarregandoVinculadas(true); setOrdensVinculadas(null)
    try {
      const r = await api.get('/erp/ordem-compra/lista', {
        params: { fornecedorCod, situacao: '00', dataDe: diasAtrasISO(30), dataAte: hojeISO() },
      })
      setOrdensVinculadas(r.data)
    } catch {
      setOrdensVinculadas([])
    } finally {
      setCarregandoVinculadas(false)
    }
  }

  const [resumoOrdem, setResumoOrdem] = useState<{ est: number; numero: number } | null>(null)
  const [resumoOrdemDados, setResumoOrdemDados] = useState<any>(null)
  const [resumoOrdemCarregando, setResumoOrdemCarregando] = useState(false)

  async function abrirResumoOrdem(numero: number) {
    const est = empresaCod ?? 1
    setResumoOrdem({ est, numero }); setResumoOrdemDados(null); setResumoOrdemCarregando(true)
    try {
      const r = await api.get(`/erp/ordem-compra/${est}/${numero}`)
      setResumoOrdemDados(r.data)
    } catch {
      setResumoOrdemDados(null)
    } finally {
      setResumoOrdemCarregando(false)
    }
  }
  function fecharResumoOrdem() { setResumoOrdem(null); setResumoOrdemDados(null) }

  function validarCabecalho(): string[] {
    const erros: string[] = []
    if (!fornecedorCod) erros.push('Fornecedor')
    if (fornecedorBloqueado) erros.push('Fornecedor está bloqueado')
    if (!dataEmissao) erros.push('Data de Emissão')
    if (dataEmissao > hojeISO()) erros.push('Data de emissão não pode ser maior que hoje')
    if (!dataRecebimento) erros.push('Data Recebimento')
    if (dataRecebimento > hojeISO()) erros.push('Data de recebimento não pode ser maior que hoje')
    if (dataEmissao && dataRecebimento && dataRecebimento < dataEmissao) erros.push('Data de recebimento anterior à emissão')
    if (avisoPeriodoEmissao) erros.push(avisoPeriodoEmissao)
    if (avisoPeriodoRecebimento) erros.push(avisoPeriodoRecebimento)
    if (!modDoctoFiscal) erros.push('Mod Docto Fiscal')
    if (['55', '57', '65', '66', '67', '99'].includes(modDoctoFiscal) && !chaveNfe.trim()) erros.push('Chave NF-e/NFS-e obrigatória para este modelo')
    if (avisoChaveCnpj) erros.push(avisoChaveCnpj)
    if (!naturezaOpCod1) erros.push('Natureza Operação')
    if (!nrFatura) erros.push('Nr Fatura')
    if (!tipoDocumento) erros.push('Tipo Documento')
    if (parseNumBr(valorInss) > 0 && !serie.trim()) erros.push('Série é obrigatória quando há retenção de INSS')
    return erros
  }

  async function handleConfirmar() {
    const erros = validarCabecalho()
    if (erros.length) { setErroSalvar(`Preencha: ${erros.join(', ')}`); return }
    setErroSalvar(''); setSalvando(true)
    try {
      const r = await api.post('/nota-entrada', {
        empcod: empresaCod,
        serie,
        numero: Number(numeroNota),
        cliCod: fornecedorCod,
        dataEmissao,
        dataRecebimento,
        ordemCompra: ordemCompra ? Number(ordemCompra) : undefined,
        naturezaCod: Number(naturezaOpCod1),
        naturezaCdf: naturezaOpCod2 ? Number(naturezaOpCod2) : undefined,
        naturezaDes: naturezaOpDes,
        observacao,
        compradorCod: compradorCod ? Number(compradorCod) : undefined,
        contaFinanceira: contaFinanceira || undefined,
        ftvNumero: Number(nrFatura),
        ftvTipdco: tipoDocumento,
        modelo: modDoctoFiscal,
        chaveNfe: chaveNfe || undefined,
        condicaoPagamento: condicaoPagamento ? Number(condicaoPagamento) : undefined,
        valorDescontoRs: parseNumBr(valorDescontoRs),
        valorSeguro: parseNumBr(valorSeguro),
        valorAcrescimo: parseNumBr(valorAcrescimo),
        tipoFrete: Number(tipoFrete),
        valorFrete: parseNumBr(valorFrete),
        valorIrrf: parseNumBr(valorIrrf),
        percIrrf: parseNumBr(percIrrf),
        valorInss: parseNumBr(valorInss),
        valorIssqn: parseNumBr(valorIssqn),
        percIss: parseNumBr(percIss),
        valorPis: fornecedorFj !== 'F' ? parseNumBr(valorPis) : 0,
        valorCofins: fornecedorFj !== 'F' ? parseNumBr(valorCofins) : 0,
        valorCsll: fornecedorFj !== 'F' ? parseNumBr(valorCsll) : 0,
        baseIcmsSt: parseNumBr(baseIcmsSt),
        valorIcmsSt: parseNumBr(valorIcmsSt),
        valorFornecido: parseNumBr(valorFornecido),
        codConsumo: Number(codConsumo) || 0,
        tipLigacao: Number(tipLigacao) || 0,
        tipAssinante: Number(tipAssinante) || 0,
        nfOriginalNumero: finalidadeNf === 'DEV' && nfOriginalNumero ? Number(nfOriginalNumero) : undefined,
        nfOriginalSerie: finalidadeNf === 'DEV' ? (nfOriginalSerie || undefined) : undefined,
        uf: fornecedorUf || undefined,
        valorMercadoria: parseNumBr(valorMercadoria),
        valorTotal: parseNumBr(valorNota),
      })
      const resultado = r.data
      setNotaSalva(resultado)
      abrirNovoItem()
    } catch (e: any) {
      setErroSalvar(e?.response?.data?.error || 'Erro ao lançar a nota fiscal')
    } finally {
      setSalvando(false)
    }
  }

  // Datas e referências
  const [dataEmissao, setDataEmissao] = useState('')
  const [avisoDataEmissao, setAvisoDataEmissao] = useState('')
  const [dataRecebimento, setDataRecebimento] = useState('')
  const [avisoDataRecebimento, setAvisoDataRecebimento] = useState('')
  // Período da TPARCOM: emissão (Digitação NFs Compra) e recebimento (Período para Compras)
  const [periodoCompras, setPeriodoCompras] = useState<PeriodoCompras | null>(null)
  const { emissao: avisoPeriodoEmissao, recebimento: avisoPeriodoRecebimento } = avisosPeriodo(periodoCompras, dataEmissao, dataRecebimento)
  useEffect(() => {
    api.get(`/nota-entrada/periodo/${empresaCod ?? 1}`).then(r => setPeriodoCompras(r.data)).catch(() => setPeriodoCompras(null))
  }, [empresaCod])
  const [ordemCompra, setOrdemCompra] = useState('')
  const dataEmissaoInputRef = useRef<HTMLInputElement>(null)

  function usarOrdemCompra(numero: number, compCod?: string, compDes?: string) {
    setOrdemCompra(String(numero))
    if (compCod && !compradorCod) {
      setCompradorCod(compCod)
      setCompradorDes(compDes || '')
    }
    // Sem isso, o Tab reseta pro início da página (cai na sidebar) ao fechar o modal.
    // Vai pra Data de Emissão — próximo campo a preencher depois de escolher a ordem.
    setTimeout(() => dataEmissaoInputRef.current?.focus(), 0)
  }
  const [compradorCod, setCompradorCod] = useState('')
  const [compradorDes, setCompradorDes] = useState('')

  // Finalidade da NF
  const [finalidadeNf, setFinalidadeNf] = useState('COM')
  const [modDoctoFiscal, setModDoctoFiscal] = useState<string>(modeloFixo || '')
  const [modDoctoFiscalDes, setModDoctoFiscalDes] = useState(modeloFixo ? 'NFS-e - Nota Fiscal de Serviço Eletrônica' : '')
  // Modelo 99 (NFS-e) só pode usar natureza de SERVIÇO (tno_csa='S') — mesma
  // regra do PACECFOP (Tno_csa x Its_esp) já usada noutras telas. Recarrega
  // sempre que o modelo mudar, porque uma nota de serviço não pode aproveitar
  // as naturezas de mercadoria que ficavam na lista antes de escolher o modelo.
  useEffect(() => {
    const csa = modDoctoFiscal === '99' ? 'S' : undefined
    api.get('/erp/naturezas', { params: { tipo: 'entrada', csa } }).then(r => setNaturezas(r.data)).catch(() => {})
  }, [modDoctoFiscal])
  const [chaveNfe, setChaveNfe] = useState('')
  // NF-e avulsa (grupo <avulsa> do XML importado): a chave leva o CNPJ do órgão, não o do fornecedor
  const [cnpjAvulsaXml, setCnpjAvulsaXml] = useState('')
  useEffect(() => {
    const chaveDigitos = chaveNfe.replace(/\D/g, '')
    // padStart: o CNPJ do fornecedor no cadastro às vezes vem sem o zero à
    // esquerda (cli_mat guardado/lido como número) — sem isso, comparar contra
    // os 14 dígitos da chave dava falso positivo pra CNPJ começado em 0.
    const cnpjFornecedor = fornecedorCnpj.replace(/\D/g, '').padStart(14, '0')
    if (chaveDigitos.length !== 44 || !fornecedorCnpj.trim()) { setAvisoChaveCnpj(''); return }
    // CNPJ do emitente ocupa as posições 7 a 20 da chave de acesso da NF-e.
    const cnpjChave = chaveDigitos.slice(6, 20)
    // série 890–899 = NF-e avulsa emitida pelo Fisco: com o XML, valida pelo CNPJ do <avulsa>; sem ele, não compara
    const serieChave = Number(chaveDigitos.slice(22, 25))
    const cnpjAvulsa = cnpjAvulsaXml.replace(/\D/g, '').padStart(14, '0')
    const divergente = serieChave >= 890 && serieChave <= 899
      ? !!cnpjAvulsaXml && cnpjChave !== cnpjAvulsa
      : cnpjChave !== cnpjFornecedor
    setAvisoChaveCnpj(divergente ? 'O CNPJ do emitente é diferente do CNPJ contido na chave da NF-e!' : '')
  }, [chaveNfe, fornecedorCnpj, cnpjAvulsaXml])
  const [importandoXml, setImportandoXml] = useState(false)
  const [avisoImportarXml, setAvisoImportarXml] = useState('')
  const [itensImportadosXml, setItensImportadosXml] = useState<ItemNota[] | null>(null)
  const [idxItemImportado, setIdxItemImportado] = useState(0)
  // Quantidade total de itens que o XML trouxe — guardada separada da fila
  // (itensImportadosXml) porque essa é esvaziada (null) quando o último item é
  // lançado, mas o Concluir Nota ainda precisa saber se faltou algum.
  const [qtdItensXml, setQtdItensXml] = useState<number | null>(null)
  const [parcelasImportadasXml, setParcelasImportadasXml] = useState<{ numero: string; vencimento: string; valor: string }[] | null>(null)
  // Chave que já foi importada com sucesso — enquanto a Chave NF-e digitada for
  // essa mesma, esconde o botão (já importou) e trava os campos vindos do XML.
  const [chaveImportada, setChaveImportada] = useState('')
  const travadoPorXml = !!chaveImportada && chaveNfe.replace(/\D/g, '') === chaveImportada
  // NFS-e importada: o XML só tem 1 serviço, mas pode virar vários itens (ex.: recapagem de pneus de
  // vários veículos). Guarda o serviço do XML e o total; o IBS/CBS de cada item vai rateado pelo valor.
  const [nfseXml, setNfseXml] = useState<{ base: ItemNota; total: number } | null>(null)
  const estiloXml: React.CSSProperties = { background: '#eff6ff', borderColor: '#93c5fd', color: '#1a6ea8', fontWeight: 600 }
  // Fornecedor do XML não cadastrado — espera confirmação antes de criar nada.
  const [fornecedorPendenteXml, setFornecedorPendenteXml] = useState<{ chave: string; cliMat: string; cliNom: string } | null>(null)

  function confirmarCadastroFornecedorXml() {
    if (!fornecedorPendenteXml) return
    importarDoXml(fornecedorPendenteXml.chave, true)
  }
  function recusarCadastroFornecedorXml() {
    setFornecedorPendenteXml(null)
    setAvisoImportarXml('Importação cancelada — cadastre o fornecedor manualmente antes de importar essa nota.')
  }

  async function importarDoXml(chaveParam?: string, criarFornecedor?: boolean) {
    const chave = (chaveParam ?? chaveNfe).replace(/\D/g, '')
    if (chave.length !== 44 && chave.length !== 50) { setAvisoImportarXml('Informe a Chave NF-e/CT-e (44 dígitos) ou NFS-e Nacional (50 dígitos) antes de importar.'); return }
    // NFS-e (50 dígitos) só na tela própria; NF-e/CT-e (44) só na de NF-e.
    if (!modeloFixo && chave.length === 50) { navigate(`${ROTA_LANCAMENTO_NFSE}?importarChave=${chave}`); return }
    if (modeloFixo && chave.length !== 50) { setAvisoImportarXml('Essa tela é só de NFS-e (chave de 50 dígitos). NF-e/CT-e se lança em "Lançamento NFE 55".'); return }
    setImportandoXml(true); setAvisoImportarXml('')
    try {
      const r = await api.get('/erp/notas-entrada/importar-xml', {
        params: { chave, empcod: empresaCod ?? 1, ...(criarFornecedor ? { criarFornecedor: 1 } : {}) },
      })
      const d = r.data

      if (d.fornecedorPendente) {
        setFornecedorPendenteXml({ chave, cliMat: d.fornecedorPendente.cliMat, cliNom: d.fornecedorPendente.cliNom })
        setImportandoXml(false)
        return
      }
      setFornecedorPendenteXml(null)

      if (d.fornecedor.criadoAgora) {
        window.open(`/configuracoes/clientes?editar=${d.fornecedor.cliCod}`, '_blank')
      }

      try {
        const rc = await api.get('/erp/clientes/lista', { params: { busca: d.fornecedor.cliCod } })
        const item = (rc.data || []).find((x: any) => x.cli_cod === d.fornecedor.cliCod) || rc.data?.[0]
        if (item) await onSelecionarFornecedor(item.cli_cod, item.cli_fan || item.cli_nom, item)
        else { setFornecedorCod(d.fornecedor.cliCod); setFornecedorDes(d.fornecedor.cliNom) }
      } catch {
        setFornecedorCod(d.fornecedor.cliCod); setFornecedorDes(d.fornecedor.cliNom)
      }

      if (!modDoctoFiscal) {
        const ehNfse = d.capa.modelo === '99'
        setModDoctoFiscal(d.capa.modelo)
        setModDoctoFiscalDes(ehNfse ? 'NFS-e - Nota Fiscal de Serviço Eletrônica' : 'Nota Fiscal Eletrônica')
        if (!tipoDocumento) {
          const buscaDoc = ehNfse ? 'NFS' : 'NFE'
          try {
            const rd = await api.get('/erp/doccad/lista', { params: { busca: buscaDoc } })
            const encontrado = (rd.data || []).find((x: any) => x.doc_cod === buscaDoc)
            if (encontrado) { setTipoDocumento(encontrado.doc_cod); setTipoDocumentoDes(encontrado.doc_des) }
          } catch { /* best-effort */ }
        }
      }

      setSerie(d.capa.serie)
      setCnpjAvulsaXml(d.capa.cnpjAvulsa || '')
      setNumeroNota(String(d.capa.numero))
      if (!nrFaturaEditada) setNrFatura(String(d.capa.numero))
      if (d.capa.dataEmissao) setDataEmissao(d.capa.dataEmissao)
      setValorFrete(fmtMoeda(String(d.capa.valorFrete)))
      setValorSeguro(fmtMoeda(String(d.capa.valorSeguro)))
      setValorDescontoRs(fmtMoeda(String(d.capa.valorDescontoRs)))
      setValorAcrescimo(fmtMoeda(String(d.capa.valorAcrescimo)))
      setBaseIcmsSt(fmtMoeda(String(d.capa.baseIcmsSt)))
      setValorIcmsSt(fmtMoeda(String(d.capa.valorIcmsSt)))
      setValorIrrf(fmtMoeda(String(d.capa.valorIrrf)))
      setValorPis(fmtMoeda(String(d.capa.valorPis)))
      setValorCofins(fmtMoeda(String(d.capa.valorCofins)))
      setValorCsll(fmtMoeda(String(d.capa.valorCsll)))
      setValorInss(fmtMoeda(String(d.capa.valorInss)))
      setValorIssqn(fmtMoeda(String(d.capa.valorIss)))
      setPercIss(fmtMoeda(String(d.capa.percIss)))
      if (d.capa.observacao) setObservacao(d.capa.observacao)
      setValorMercadoria(fmtMoeda(String(d.capa.valorMercadoria)))
      setValorNota(fmtMoeda(String(d.capa.valorTotal)))
      // Prévia dos totais de tributo direto do XML — antes de lançar os itens
      // (o useEffect abaixo recalcula pelos itens de verdade assim que forem
      // adicionados, então isso só serve de referência enquanto não lança nada).
      if (d.capa.baseIcms != null) setBaseTribIcms(fmtMoeda(String(d.capa.baseIcms)))
      if (d.capa.valorIcms != null) setValorIcms(fmtMoeda(String(d.capa.valorIcms)))
      if (d.capa.valorIpi != null) setValorIpi(fmtMoeda(String(d.capa.valorIpi)))
      if (d.capa.valorIbs != null) setValorIbs(fmtMoeda(String(d.capa.valorIbs)))
      if (d.capa.valorCbs != null) setValorCbs(fmtMoeda(String(d.capa.valorCbs)))

      const nMoeda = (v: number) => fmtMoeda(String(v))
      const itensConvertidos: ItemNota[] = (d.itens || []).map((it: any) => ({
        ...ITEM_VAZIO,
        hora: horaAtualStr(),
        itemCod: it.itemCod, itemDes: it.itemDes, descComplementar: it.descComplementar, itemUn: it.itemUn,
        quantidade: fmtQtd(String(it.quantidade)), valorTotal: nMoeda(it.valorTotal),
        valorUnitario: it.valorUnitario ? fmtUnit(it.valorUnitario) : '',
        ncmCod: it.ncmCod,
        nbsCod: it.nbsCod || '', nbsDes: it.nbsDes || '',
        itemValorFrete: nMoeda(it.itemValorFrete), itemValorAcrescimo: nMoeda(it.itemValorAcrescimo),
        itemValorSeguro: nMoeda(it.itemValorSeguro), itemValorDesconto: nMoeda(it.itemValorDesconto),
        icmsBase: nMoeda(it.icmsBase), icmsPerc: nMoeda(it.icmsPerc), icmsValor: nMoeda(it.icmsValor), icmsCst: it.icmsCst,
        icmsStBase: nMoeda(it.icmsStBase), icmsStPerc: nMoeda(it.icmsStPerc), icmsStValor: nMoeda(it.icmsStValor),
        ipiBase: nMoeda(it.ipiBase), ipiPerc: nMoeda(it.ipiPerc), ipiValor: nMoeda(it.ipiValor), ipiCst: it.ipiCst,
        ipiXmlBase: nMoeda(it.ipiBase), ipiXmlPerc: nMoeda(it.ipiPerc), ipiXmlValor: nMoeda(it.ipiValor),
        // pisBase/cofinsBase NÃO vêm do XML — ficam no default (0,00) até o motor
        // de cálculo (CFOP/Natureza) preencher. O que a nota do fornecedor declarou
        // vai só nos campos *Xml* abaixo, como referência.
        pisXmlBase: nMoeda(it.pisXmlBase), pisXmlAliq: nMoeda(it.pisXmlAliq), pisXmlValor: nMoeda(it.pisXmlValor), pisXmlCst: it.pisXmlCst,
        cofinsXmlBase: nMoeda(it.cofinsXmlBase), cofinsXmlAliq: nMoeda(it.cofinsXmlAliq), cofinsXmlValor: nMoeda(it.cofinsXmlValor), cofinsXmlCst: it.cofinsXmlCst,
        ibsCbsCst: it.ibsCbsCst, ibsCbsClassTrib: it.ibsCbsClassTrib,
        cbsBase: nMoeda(it.cbsBase), cbsBcEf: nMoeda(it.cbsBcEf), cbsAliRef: nMoeda(it.cbsAliRef),
        cbsRedAli: nMoeda(it.cbsRedAli), cbsAliEf: nMoeda(it.cbsAliEf), cbsValor: nMoeda(it.cbsValor),
        // O XML não traz a UF dentro de gIBSUF (é implícita: sempre a UF de destino
        // da operação, ou seja, a da própria empresa recebendo a mercadoria).
        ibsUfEst: it.ibsUfEst || empresaAtual?.empest || '', ibsUfBcEf: nMoeda(it.ibsUfBcEf), ibsUfAliRef: nMoeda(it.ibsUfAliRef),
        ibsUfRedAli: nMoeda(it.ibsUfRedAli), ibsUfAliEf: nMoeda(it.ibsUfAliEf), ibsUfValor: nMoeda(it.ibsUfValor),
        ibsMunBase: nMoeda(it.ibsMunBase), ibsMunBcEf: nMoeda(it.ibsMunBcEf), ibsMunAliRef: nMoeda(it.ibsMunAliRef),
        ibsMunRedAli: nMoeda(it.ibsMunRedAli), ibsMunAliEf: nMoeda(it.ibsMunAliEf), ibsMunValor: nMoeda(it.ibsMunValor),
        isBase: nMoeda(it.isBase), isAliq: nMoeda(it.isAliq),
        ibsCbsMono: it.ibsCbsMono || [],
      }))
      setItensImportadosXml(itensConvertidos)
      const nfseUmServico = d.capa.modelo === '99' && itensConvertidos.length === 1
      setNfseXml(nfseUmServico ? { base: itensConvertidos[0], total: parseNumBr(itensConvertidos[0].valorTotal) } : null)
      // Na NFS-e quem confere é a soma dos valores (concluirNota), não a quantidade de itens
      setQtdItensXml(nfseUmServico ? null : itensConvertidos.length)
      setIdxItemImportado(0)
      if (itensConvertidos.length) setItemForm(itensConvertidos[0])

      setParcelasImportadasXml(
        (d.parcelas || []).length
          ? d.parcelas.map((p: any) => ({ numero: p.numero, vencimento: p.vencimento, valor: fmtMoeda(String(p.valor)) }))
          : null
      )

      setAvisoImportarXml((d.avisos || []).join(' '))
      setChaveImportada(chave)
    } catch (e: any) {
      setAvisoImportarXml(e?.response?.data?.error || 'Erro ao importar XML.')
    } finally {
      setImportandoXml(false)
    }
  }

  // Veio do Manifestador do Destinatário (botão "Importar XML") com a chave na
  // URL — preenche e já dispara a importação sozinho, sem o usuário ter que
  // colar a chave de novo. Só dispara uma vez (espera empresaCod estar pronto).
  const importarUrlDisparado = useRef(false)
  useEffect(() => {
    // Espera empresaAtual (não só empresaCod) — empresaCod hidrata na hora
    // (localStorage), mas a lista de empresas ainda pode não ter chegado da API
    // ainda; sem empresaAtual, a UF do IBS Estadual não tinha como ser preenchida.
    if (!importarChaveUrl || importarUrlDisparado.current || !empresaAtual) return
    importarUrlDisparado.current = true
    setChaveNfe(importarChaveUrl)
    importarDoXml(importarChaveUrl)
  }, [importarChaveUrl, empresaAtual])

  const [naturezaOpCod1, setNaturezaOpCod1] = useState('')
  const [naturezaOpCod2, setNaturezaOpCod2] = useState('')
  const [naturezaOpDes, setNaturezaOpDes] = useState('')
  const [nfOriginalNumero, setNfOriginalNumero] = useState('')
  const [nfOriginalSerie, setNfOriginalSerie] = useState('')

  function onSelecionarNatureza(cod: string) {
    setNaturezaOpCod1(cod)
    const nat = naturezas.find(n => String(n.tno_cod) === cod)
    setNaturezaOpCod2(nat ? String(nat.tno_cdf) : '')
    setNaturezaOpDes(nat?.tno_des || '')
  }
  const [valorFornecido, setValorFornecido] = useState('0,00')
  const [codConsumo, setCodConsumo] = useState('0')
  const [tipLigacao, setTipLigacao] = useState('0')
  const [tipAssinante, setTipAssinante] = useState('0')

  // Documento e financeiro
  const [tipoDocumento, setTipoDocumento] = useState('')
  const [tipoDocumentoDes, setTipoDocumentoDes] = useState('')
  // Tela de NFS-e já abre com o Tipo de Documento NFS (mesmo autofill da importação).
  useEffect(() => {
    if (!modeloFixo) return
    api.get('/erp/doccad/lista', { params: { busca: 'NFS' } }).then(r => {
      const d = (r.data || []).find((x: any) => x.doc_cod === 'NFS')
      if (d) { setTipoDocumento(t => t || d.doc_cod); setTipoDocumentoDes(t => t || d.doc_des) }
    }).catch(() => {})
  }, [modeloFixo])
  const [nrFatura, setNrFatura] = useState('')
  const [nrFaturaEditada, setNrFaturaEditada] = useState(false)
  const [contaFinanceira, setContaFinanceira] = useState('')
  const [contaFinanceiraDes, setContaFinanceiraDes] = useState('')
  const [condicaoPagamento, setCondicaoPagamento] = useState('')
  const [condicaoPagamentoDes, setCondicaoPagamentoDes] = useState('')

  // Desconto e frete
  const [valorDescontoRs, setValorDescontoRs] = useState('0,00')
  const [valorSeguro, setValorSeguro] = useState('0,00')
  const [valorAcrescimo, setValorAcrescimo] = useState('0,00')
  const [tipoFrete, setTipoFrete] = useState('9')
  const [valorFrete, setValorFrete] = useState('0,00')

  // Impostos retidos
  const [valorIrrf, setValorIrrf] = useState('0,00')
  const [percIrrf, setPercIrrf] = useState('0,00')
  const [valorInss, setValorInss] = useState('0,00')
  const [valorIssqn, setValorIssqn] = useState('0,00')
  const [percIss, setPercIss] = useState('0,00')
  const [valorPis, setValorPis] = useState('0,00')
  const [valorCofins, setValorCofins] = useState('0,00')
  const [valorCsll, setValorCsll] = useState('0,00')
  const [baseIcmsSt, setBaseIcmsSt] = useState('0,00')
  const [valorIcmsSt, setValorIcmsSt] = useState('0,00')

  // Observação
  const [observacao, setObservacao] = useState('')

  // Totais
  const [quantidade, setQuantidade] = useState('0,000')
  const [baseTribIcms, setBaseTribIcms] = useState('0,00')
  const [valorIcms, setValorIcms] = useState('0,00')
  const [baseTribIpi, setBaseTribIpi] = useState('0,00')
  const [valorIpi, setValorIpi] = useState('0,00')
  const [valorMercadoria, setValorMercadoria] = useState('0,00')
  const [valorNota, setValorNota] = useState('0,00')
  const [valorIbs, setValorIbs] = useState('0,00')
  const [valorCbs, setValorCbs] = useState('0,00')

  // Itens da nota
  const [itens, setItens] = useState<ItemNota[]>([])
  const [modalItemAberto, setModalItemAberto] = useState(false)
  const [itemEditandoIdx, setItemEditandoIdx] = useState<number | null>(null)
  const [tabItem, setTabItem] = useState<'geral' | 'tributacao'>('geral')
  // Aba do IBS/CBS no item; null = automática (monofásica quando o item tem esse grupo)
  const [abaIbsCbs, setAbaIbsCbs] = useState<'regular' | 'mono' | null>(null)
  const [itemForm, setItemForm] = useState<ItemNota>(ITEM_VAZIO)
  useEffect(() => { setAbaIbsCbs(null) }, [itemForm.ibsCbsMono])
  // Ajusta a altura do textarea da Descrição Complementar pro tamanho real do texto
  // renderizado — uma estimativa de "caracteres por linha" não bate com o quanto o
  // navegador realmente quebra a linha (depende de fonte/largura), cortava texto.
  const descComplementarRef = useRef<HTMLTextAreaElement>(null)
  useLayoutEffect(() => {
    const el = descComplementarRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
    // modalItemAberto entra na dependência porque o texto às vezes já vem
    // preenchido (XML/edição) ANTES do textarea existir no DOM — sem isso, o
    // efeito não reroda quando o modal abre e a caixa fica com a altura velha.
  }, [itemForm.descComplementar, modalItemAberto])
  // O item aberto agora veio da fila de importação do XML? Trava os campos que
  // vieram de lá (destaque azul) — só enquanto for o item importado, não depois
  // de já ter sido salvo/reaberto pra edição normal.
  const [itemFormTravadoXml, setItemFormTravadoXml] = useState(false)
  const [avisoOrdemItem, setAvisoOrdemItem] = useState('')
  const [carregandoItemOrdem, setCarregandoItemOrdem] = useState(false)
  const [erroItem, setErroItem] = useState('')
  const [salvandoItem, setSalvandoItem] = useState(false)
  // Abre automaticamente depois de salvar um item novo com Cód. Pneu preenchido —
  // equivalente ao fluxo legado TREVITE → PREPENU → TCUSPNU.
  // Fila de Recapagem/Conserto: um pneu por item lançado, recebido um de cada vez (NFS-e com vários recapes)
  type RecapagemPendente = { pnvLin: number; pnucod: number; procedimento: 'S' | 'N'; valorInicial: number }
  const [filaRecapagem, setFilaRecapagem] = useState<RecapagemPendente[]>([])
  const modalRecapagem = filaRecapagem[0] ?? null
  const enfileirarRecapagem = (r: RecapagemPendente) => setFilaRecapagem(f => [...f.filter(x => x.pnvLin !== r.pnvLin), r])
  const proximaRecapagem = () => setFilaRecapagem(f => f.slice(1))

  function campoItem<K extends keyof ItemNota>(campo: K, valor: ItemNota[K]) {
    setItemForm(f => ({ ...f, [campo]: valor }))
  }

  // Rateio do IBS/CBS da NFS-e pelo peso do item (valor do item / total da nota). O item que fecha
  // o total leva a diferença, pra soma dos itens bater no centavo com o XML.
  function rateioNfse(f: ItemNota, valor: number, outros: ItemNota[]): ItemNota {
    if (!nfseXml) return f
    const somaOutros = outros.reduce((s, it) => s + parseNumBr(it.valorTotal), 0)
    const fechaTotal = Math.abs(nfseXml.total - somaOutros - valor) < 0.005
    const r = { ...f }
    for (const c of CAMPOS_RATEIO_NFSE) {
      const xml = parseNumBr(nfseXml.base[c])
      const v = fechaTotal
        ? xml - outros.reduce((s, it) => s + parseNumBr(it[c]), 0)
        : nfseXml.total ? Math.round(xml * valor / nfseXml.total * 100) / 100 : 0
      r[c] = fmtMoeda(String(Math.max(0, Math.round(v * 100) / 100)))
    }
    return r
  }
  const somaValorItens = (l: ItemNota[]) => l.reduce((s, it) => s + parseNumBr(it.valorTotal), 0)
  // Item novo da NFS-e que fecha o total: o botão vira "Concluir" (salva o item e conclui a nota)
  const itemFechaNfse = !!nfseXml && itemEditandoIdx === null &&
    Math.abs(nfseXml.total - somaValorItens(itens) - parseNumBr(itemForm.valorTotal)) < 0.005
  const [concluirAgora, setConcluirAgora] = useState(false)
  // Próximo item da NFS-e: mesmo serviço do XML, já com o valor que falta pra fechar a nota
  function itemRestanteNfse(lancados: ItemNota[], anterior?: ItemNota): ItemNota | null {
    if (!nfseXml) return null
    const restante = Math.round((nfseXml.total - somaValorItens(lancados)) * 100) / 100
    if (restante < 0.01) return null
    const f = {
      ...nfseXml.base, hora: horaAtualStr(), valorTotal: fmtMoeda(String(restante)),
      // Natureza/CFOP é digitada item a item, não herda da capa nem do item anterior
      naturezaCod: '', cfopCod: '', cfopDes: '',
    }
    return rateioNfse(f, restante, lancados)
  }

  // Totais da capa recalculados a partir da soma dos itens lançados — sem isso
  // "Val da Nota" ficava sempre 0,00 e o Financeiro não tinha valor nenhum pra
  // gerar as parcelas. ICMS/IPI/IBS/CBS entraram depois (ficavam travados em
  // 0,00 pra sempre, mesmo com itens já lançados com esses valores).
  //
  // Enquanto nenhum item ainda foi lançado (itens vazio), usa a fila importada do
  // XML como prévia (itensImportadosXml já tem todos os valores, mesmo antes de
  // clicar em "Adicionar Item") — sem isso os totais só apareciam certos depois de
  // lançar tudo. Assim que o primeiro item é lançado de verdade, volta a somar só
  // os itens realmente salvos (reflete edições que o usuário tenha feito no item).
  useEffect(() => {
    const fonte = itens.length > 0 ? itens : (itensImportadosXml ?? [])
    const somaItens = fonte.reduce((s, it) => s + parseNumBr(it.valorTotal), 0)
    const somaQtd = fonte.reduce((s, it) => s + parseNumBr(it.quantidade), 0)
    setValorMercadoria(fmtMoeda(String(somaItens)))
    setQuantidade(somaQtd.toLocaleString('pt-BR', { minimumFractionDigits: 3 }))
    const soma = (campo: keyof ItemNota) => fonte.reduce((s, it) => s + parseNumBr(it[campo] as string), 0)
    // vNF = produtos + frete/seguro/outras − desconto + IPI + ICMS-ST (senão não bate com a duplicata)
    const total = somaItens + parseNumBr(valorFrete) + parseNumBr(valorSeguro) + parseNumBr(valorAcrescimo) - parseNumBr(valorDescontoRs)
      + soma('ipiValor') + soma('icmsStValor')
    setValorNota(fmtMoeda(String(round2(total))))
    setBaseTribIcms(fmtMoeda(String(soma('icmsBase'))))
    setValorIcms(fmtMoeda(String(soma('icmsValor'))))
    setBaseTribIpi(fmtMoeda(String(soma('ipiBase'))))
    setValorIpi(fmtMoeda(String(soma('ipiValor'))))
    setValorIbs(fmtMoeda(String(soma('ibsUfValor') + soma('ibsMunValor'))))
    setValorCbs(fmtMoeda(String(soma('cbsValor'))))
  }, [itens, itensImportadosXml, valorFrete, valorSeguro, valorAcrescimo, valorDescontoRs])

  // Recalcula ICMS/IPI/PIS/COFINS/INSS/DIFAL automaticamente (igual ao GeneXus,
  // que refaz o cálculo sempre que Natureza/Item/Valor mudam) — debounced pra não
  // bater no back-end a cada tecla. Os campos continuam editáveis: é só o ponto
  // de partida, o usuário ajusta na mão se precisar.
  const debounceImpostosRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    const tnoItem = itemForm.naturezaCod
    if (!modalItemAberto || !tnoItem || !itemForm.itemCod) return
    if (debounceImpostosRef.current) clearTimeout(debounceImpostosRef.current)
    debounceImpostosRef.current = setTimeout(async () => {
      try {
        const r = await api.post('/erp/itens-nota/calcular-impostos', {
          empresaCod: empresaCod ?? 1,
          empresaUf: empresaAtual?.empest || '',
          tnocod: Number(tnoItem),
          itemCod: Number(itemForm.itemCod),
          clienteFj: fornecedorFj,
          clienteUf: fornecedorUf,
          valorMercadoria: parseNumBr(itemForm.valorTotal),
          valorAcrescimo: parseNumBr(itemForm.itemValorAcrescimo),
          valorFrete: parseNumBr(itemForm.itemValorFrete),
          valorSeguro: parseNumBr(itemForm.itemValorSeguro),
          valorDesconto: parseNumBr(itemForm.itemValorDesconto),
        })
        const d = r.data
        setItemForm(f => {
          // Item do XML com IPI: mantém base/alíquota/valor do documento (a Natureza só define o CST)
          const ipiDoXml = parseNumBr(f.ipiXmlValor) > 0
          const ipiValor = ipiDoXml ? parseNumBr(f.ipiXmlValor) : d.ipi.valor
          return {
          ...f,
          icmsCst: d.icms.cst, icmsBase: fmtMoeda(String(d.icms.base)), icmsPerc: fmtMoeda(String(d.icms.perc)), icmsValor: fmtMoeda(String(d.icms.valor)),
          ipiCst: d.ipi.cst,
          ipiBase: ipiDoXml ? f.ipiXmlBase : fmtMoeda(String(d.ipi.base)),
          ipiPerc: ipiDoXml ? f.ipiXmlPerc : fmtMoeda(String(d.ipi.perc)),
          ipiValor: fmtMoeda(String(ipiValor)),
          pisCst: d.pis.cst, pisBase: fmtMoeda(String(d.pis.base)), pisPerc: fmtMoeda(String(d.pis.perc)), pisValor: fmtMoeda(String(d.pis.valor)),
          cofinsCst: d.cofins.cst, cofinsBase: fmtMoeda(String(d.cofins.base)), cofinsPerc: fmtMoeda(String(d.cofins.perc)), cofinsValor: fmtMoeda(String(d.cofins.valor)),
          inssBase: fmtMoeda(String(d.inss.base)), inssPerc: fmtMoeda(String(d.inss.perc)), inssValor: fmtMoeda(String(d.inss.valor)),
          difal: fmtMoeda(String(d.difal.valor)),
          valorContabil: fmtMoeda(String(round2(d.valorContabil + ipiValor))),
          }
        })
      } catch { /* cálculo automático é best-effort; usuário preenche na mão se falhar */ }
    }, 600)
    return () => { if (debounceImpostosRef.current) clearTimeout(debounceImpostosRef.current) }
  }, [
    modalItemAberto, itemForm.naturezaCod, itemForm.itemCod, itemForm.valorTotal,
    itemForm.itemValorAcrescimo, itemForm.itemValorFrete, itemForm.itemValorSeguro, itemForm.itemValorDesconto,
    empresaCod, empresaAtual, fornecedorFj, fornecedorUf,
  ])

  // Financeiro (fatura/duplicatas) — equivalente à tela legada TNOTFAT, que no
  // ERP abre depois que todos os itens da nota já foram lançados.
  const [modalFinanceiroAberto, setModalFinanceiroAberto] = useState(false)
  const [parcelas, setParcelas] = useState<{ numero: string; vencimento: string; valor: string }[]>([])
  const [erroFinanceiro, setErroFinanceiro] = useState('')
  const [salvandoFinanceiro, setSalvandoFinanceiro] = useState(false)
  const [financeiroSalvo, setFinanceiroSalvo] = useState(false)
  // Usuário excluiu o financeiro de propósito: libera concluir mesmo com natureza exigindo.
  const [financeiroDispensado, setFinanceiroDispensado] = useState(false)
  const [excluindoFinanceiro, setExcluindoFinanceiro] = useState(false)

  async function gerarParcelasIniciais(): Promise<{ numero: string; vencimento: string; valor: string }[]> {
    const total = totalNotaFinanceiro
    if (condicaoPagamento) {
      try {
        const r = await api.get(`/condicao-pagamento/${condicaoPagamento}`)
        const linhas: { num: number; dias: number; percentual: number }[] = r.data?.parcelas || []
        if (linhas.length) {
          let acumulado = 0
          return linhas.map((l, idx) => {
            let valor = round2(total * (l.percentual / 100))
            if (idx === linhas.length - 1) valor = round2(total - acumulado)
            else acumulado = round2(acumulado + valor)
            return {
              numero: String(l.num).padStart(2, '0'),
              vencimento: somarDiasISO(dataEmissao, l.dias),
              valor: fmtMoeda(String(valor)),
            }
          })
        }
      } catch { /* condição sem parcelas cadastradas ou erro na busca — cai no fallback abaixo */ }
    }
    // Sem Condição de Pagamento informada: uma parcela única com o valor total.
    return [{ numero: '01', vencimento: dataEmissao, valor: fmtMoeda(String(total)) }]
  }

  async function abrirFinanceiro() {
    if (filaRecapagem.length) { setAvisoConcluir('Receba os pneus de Recapagem/Conserto antes de gerar a fatura.'); return }
    setErroFinanceiro('')
    // Só (re)gera as parcelas se ainda não tem nenhuma carregada — sem essa guarda,
    // fechar o modal sem salvar (ex: usuário fecha, "Concluir Nota" reabre sozinho
    // porque a CFOP exige financeiro) jogava fora tanto a parcela importada do XML
    // (<cobr><dup>) quanto qualquer edição manual, e regerava do zero pela Condição
    // de Pagamento.
    if (!parcelas.length) {
      if (parcelasImportadasXml?.length) {
        setParcelas(parcelasImportadasXml)
      } else {
        setParcelas(await gerarParcelasIniciais())
      }
    }
    setModalFinanceiroAberto(true)
  }

  function adicionarParcela() {
    setParcelas(list => [...list, { numero: String(list.length + 1).padStart(2, '0'), vencimento: dataEmissao, valor: '0,00' }])
  }
  function removerParcela(idx: number) {
    setParcelas(list => list.filter((_, i) => i !== idx))
  }
  function alterarParcela(idx: number, campo: 'numero' | 'vencimento' | 'valor', valor: string) {
    setParcelas(list => list.map((p, i) => i === idx ? { ...p, [campo]: valor } : p))
  }

  const totalParcelas = parcelas.reduce((s, p) => s + parseNumBr(p.valor), 0)
  // Fatura sai líquida das retenções da capa, igual o FrotaWin (NFS-e 480501: 540,55 → 493,79)
  const totalRetidoNota = round2(
    parseNumBr(valorIrrf) + parseNumBr(valorInss) + parseNumBr(valorIssqn) +
    (fornecedorFj !== 'F' ? parseNumBr(valorPis) + parseNumBr(valorCofins) + parseNumBr(valorCsll) : 0),
  )
  const totalNotaFinanceiro = round2(parseNumBr(valorNota) - totalRetidoNota)
  const parcelasBatemComTotal = Math.abs(totalParcelas - totalNotaFinanceiro) < 0.01

  async function salvarFinanceiroClick() {
    if (!contaFinanceira) { setErroFinanceiro('Conta Financeira é obrigatória.'); return }
    if (!parcelasBatemComTotal) {
      setErroFinanceiro(`A soma das parcelas (R$ ${fmtMoeda(String(totalParcelas))}) precisa bater com o total da nota${totalRetidoNota > 0 ? ' menos as retenções' : ''} (R$ ${fmtMoeda(String(totalNotaFinanceiro))}).`)
      return
    }
    setErroFinanceiro(''); setSalvandoFinanceiro(true)
    try {
      const r = await api.post('/erp/notas-entrada/financeiro', {
        empcod: empresaCod,
        numero: notaSalva?.numero,
        serie: notaSalva?.serie,
        cliente: fornecedorCod,
        modelo: modDoctoFiscal,
        dataEmissao,
        contaCod: contaFinanceira,
        parcelas: parcelas.map(p => ({ numero: p.numero, vencimento: p.vencimento, valor: parseNumBr(p.valor) })),
      })
      setModalFinanceiroAberto(false)
      const titulos: string[] = r.data?.titulosRetencao || []
      if (titulos.length) window.alert(`Títulos a pagar das retenções gerados:\n\n${titulos.join('\n')}`)
      setFinanceiroSalvo(true)
      setFinanceiroDispensado(false)
    } catch (e: any) {
      setErroFinanceiro(e?.response?.data?.error || 'Erro ao salvar financeiro')
    } finally {
      setSalvandoFinanceiro(false)
    }
  }

  async function excluirFinanceiroClick() {
    if (!window.confirm('Excluir o financeiro desta nota? A nota poderá ser concluída sem Contas a Pagar.')) return
    setErroFinanceiro(''); setExcluindoFinanceiro(true)
    try {
      if (notaSalva?.numero && fornecedorCod) {
        await api.delete('/erp/notas-entrada/financeiro', {
          params: { empcod: empresaCod, numero: notaSalva.numero, cliente: fornecedorCod, modelo: modDoctoFiscal },
        })
      }
      setParcelas([])
      setParcelasImportadasXml(null)
      setFinanceiroSalvo(false)
      setFinanceiroDispensado(true)
      setModalFinanceiroAberto(false)
      setAvisoConcluir('')
    } catch (e: any) {
      setErroFinanceiro(e?.response?.data?.error || 'Erro ao excluir financeiro')
    } finally {
      setExcluindoFinanceiro(false)
    }
  }

  const [avisoConcluir, setAvisoConcluir] = useState('')
  // "✓ Concluir" do item que fecha a NFS-e: conclui depois que o item entrou na lista
  // (espera receber os pneus de recapagem/conserto pendentes antes de seguir pra fatura)
  useEffect(() => {
    if (!concluirAgora || filaRecapagem.length) return
    setConcluirAgora(false)
    concluirNota()
  }, [concluirAgora, itens, filaRecapagem.length])

  async function concluirNota() {
    if (!itens.length) { setAvisoConcluir('Adicione ao menos um item antes de concluir a nota.'); return }
    if (filaRecapagem.length) { setAvisoConcluir('Receba os pneus de Recapagem/Conserto antes de concluir a nota.'); return }
    if (qtdItensXml !== null && itens.length < qtdItensXml) {
      setAvisoConcluir(`A nota veio do XML com ${qtdItensXml} item(ns) — faltam lançar ${qtdItensXml - itens.length} antes de concluir.`)
      return
    }
    // NFS-e importada: a soma dos itens tem que fechar com o total do XML
    if (nfseXml) {
      const dif = Math.round((nfseXml.total - somaValorItens(itens)) * 100) / 100
      if (Math.abs(dif) >= 0.01) {
        setAvisoConcluir(`A soma dos itens (R$ ${fmtMoeda(String(somaValorItens(itens)))}) não fecha com o total da NFS-e (R$ ${fmtMoeda(String(nfseXml.total))}) — ${dif > 0 ? 'faltam' : 'sobram'} R$ ${fmtMoeda(String(Math.abs(dif)))}.`)
        return
      }
    }
    const natureza = naturezas.find(n => String(n.tno_cod) === naturezaOpCod1)
    const exigeFinanceiro = natureza?.tno_ctp?.trim().toUpperCase() === 'S'
    if (exigeFinanceiro && !financeiroSalvo && !financeiroDispensado) {
      setAvisoConcluir('Essa Natureza de Operação integra com Contas a Pagar — informe o Financeiro antes de concluir.')
      abrirFinanceiro()
      return
    }
    setAvisoConcluir('')
    // Só marca a nota como "digitada" (Manifestador) aqui, depois de confirmar que
    // todos os itens do XML foram lançados — nunca na confirmação da capa.
    if (chaveNfe.trim()) {
      try {
        const r = await api.post('/nota-entrada/concluir', { chaveNfe: chaveNfe.trim(), empcod: notaSalva?.empcod ?? empresaCod ?? 1 })
        // Confirmação da Operação automática (TPARNFE) que não registrou: avisa pra mandar pelo Manifestador
        const c = r.data?.confirmacao
        if (c?.enviado && !c.registrada) {
          window.alert(`Nota concluída, mas a Confirmação da Operação NÃO foi registrada na SEFAZ:\n${[c.cStat, c.xMotivo || c.erro].filter(Boolean).join(' - ')}\n\nEnvie a confirmação pelo Manifestador.`)
        }
      }
      catch { /* nunca bloqueia a conclusão por causa disso — best-effort */ }
    }
    setNotaConcluida(true)
    const destino =
      modDoctoFiscal === '99' ? '/compras/manifestacao/nfse' :
      ['57', '67'].includes(modDoctoFiscal) ? '/compras/manifestacao/cte' :
      '/compras/manifestacao/nfe'
    navigate(destino)
  }

  // Capa salva e nenhum item: segura a saída (menu, Sair, fechar aba) pra não deixar nota sem item no banco
  const [saidaPendente, setSaidaPendente] = useState<(() => void) | null>(null)
  const [excluindoCapa, setExcluindoCapa] = useState(false)
  const [erroExcluirCapa, setErroExcluirCapa] = useState('')
  const capaSemItens = !!notaSalva && itens.length === 0
  useEffect(() => {
    if (!capaSemItens) return
    const desfazer = registrarGuardaSaida(prosseguir => { setSaidaPendente(() => prosseguir); return true })
    const aoFechar = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', aoFechar)
    return () => { desfazer(); window.removeEventListener('beforeunload', aoFechar) }
  }, [capaSemItens])

  // Nota nova com itens e sem "Concluir": a capa e os itens já estão gravados, então sair só concluindo
  // ou excluindo a nota (estorno) — senão ficava lançada pela metade
  const [notaConcluida, setNotaConcluida] = useState(false)
  const [saidaNaoConcluida, setSaidaNaoConcluida] = useState<(() => void) | null>(null)
  const [excluindoNota, setExcluindoNota] = useState(false)
  const [erroExcluirNota, setErroExcluirNota] = useState('')
  const notaNaoConcluida = !!notaSalva && itens.length > 0 && !notaConcluida
  useEffect(() => {
    if (!notaNaoConcluida) return
    const desfazer = registrarGuardaSaida(prosseguir => { setSaidaNaoConcluida(() => prosseguir); return true })
    const aoFechar = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', aoFechar)
    return () => { desfazer(); window.removeEventListener('beforeunload', aoFechar) }
  }, [notaNaoConcluida])

  // Mesma rotina da tela Estorno de Nota (apaga itens, rateio, financeiro e OS gerada, desmarca no Manifestador)
  async function excluirNotaESair() {
    if (!notaSalva || !saidaNaoConcluida) return
    setExcluindoNota(true); setErroExcluirNota('')
    try {
      await api.post('/erp/estorno-nota', { empcod: notaSalva.empcod, serie: notaSalva.serie, numero: notaSalva.numero, cliCod: fornecedorCod })
      const prosseguir = saidaNaoConcluida
      setSaidaNaoConcluida(null); setNotaSalva(null); setItens([])
      setTimeout(prosseguir, 50) // deixa a guarda/beforeunload sair antes de navegar
    } catch (e: any) {
      setErroExcluirNota(e?.response?.data?.error || 'Erro ao excluir a nota')
    } finally {
      setExcluindoNota(false)
    }
  }

  // "Lançar Nova Nota" passa pelas mesmas guardas do menu
  function lancarNovaNota() {
    const recarregar = () => window.location.reload()
    if (capaSemItens) setSaidaPendente(() => recarregar)
    else if (notaNaoConcluida) setSaidaNaoConcluida(() => recarregar)
    else recarregar()
  }

  async function excluirCapaESair() {
    if (!notaSalva || !saidaPendente) return
    setExcluindoCapa(true); setErroExcluirCapa('')
    try {
      await api.post('/nota-entrada/excluir-sem-itens', {
        empcod: notaSalva.empcod, serie: notaSalva.serie, numero: notaSalva.numero, cliCod: fornecedorCod,
      })
      const prosseguir = saidaPendente
      setSaidaPendente(null); setNotaSalva(null)
      prosseguir()
    } catch (e: any) {
      setErroExcluirCapa(e?.response?.data?.error || 'Erro ao excluir a nota')
    } finally {
      setExcluindoCapa(false)
    }
  }

  // Modal "Itens da Ordem de Serviço" (equivalente à tela legada WORDSIT)
  const [modalOSAberto, setModalOSAberto] = useState(false)
  const [osItens, setOsItens] = useState<any[] | null>(null)
  const [carregandoOS, setCarregandoOS] = useState(false)
  const [osDataDe, setOsDataDe] = useState('')
  const [osDataAte, setOsDataAte] = useState('')
  const [osTodas, setOsTodas] = useState(false)

  // Padrão só confirmadas; "Mostrar todas" traz tudo menos as canceladas.
  async function buscarOrdensServico(dataDe: string, dataAte: string, todas = osTodas) {
    setCarregandoOS(true); setOsItens(null)
    try {
      const r = await api.get('/erp/ordser/itens', {
        params: { veiculo: itemForm.veiculoCod, dataDe, dataAte, todas: todas ? 'S' : undefined },
      })
      setOsItens(r.data)
    } catch {
      setOsItens([])
    } finally {
      setCarregandoOS(false)
    }
  }

  function abrirOrdensServico() {
    if (!itemForm.veiculoCod) return
    const ate = hojeISO()
    const de = diasAtrasISO(30)
    setOsDataDe(de); setOsDataAte(ate); setOsTodas(false)
    setModalOSAberto(true)
    buscarOrdensServico(de, ate, false)
  }

  // "Quer finalizar a OS?" — pergunta que aparece depois de gerar/vincular/incluir
  // serviço numa OS que ainda não está executada (setor + data de execução).
  const [modalFinalizarOSAberto, setModalFinalizarOSAberto] = useState(false)
  const [osParaFinalizar, setOsParaFinalizar] = useState<{ ordnum: number; ordlin: number } | null>(null)
  const [setorCod, setSetorCod] = useState('')
  const [setorDes, setSetorDes] = useState('')
  const [dataExecucaoOS, setDataExecucaoOS] = useState('')
  const [salvandoFinalizacaoOS, setSalvandoFinalizacaoOS] = useState(false)
  const [erroFinalizacaoOS, setErroFinalizacaoOS] = useState('')

  function abrirFinalizarOS(ordnum: number, ordlin: number) {
    setOsParaFinalizar({ ordnum, ordlin })
    setSetorCod(''); setSetorDes(''); setDataExecucaoOS(dataEmissao || hojeISO())
    setErroFinalizacaoOS('')
    setModalFinalizarOSAberto(true)
  }

  async function confirmarFinalizacaoOS(finalizarOS: boolean) {
    if (!osParaFinalizar) return
    if (!setorCod) { setErroFinalizacaoOS('Informe o Setor.'); return }
    setSalvandoFinalizacaoOS(true); setErroFinalizacaoOS('')
    try {
      await api.post(`/erp/ordem-servico/${osParaFinalizar.ordnum}/${osParaFinalizar.ordlin}/finalizar`, {
        setor: setorCod, dataExecucao: dataExecucaoOS, finalizarOS,
      })
      setModalFinalizarOSAberto(false)
      setOsParaFinalizar(null)
    } catch (e: any) {
      setErroFinalizacaoOS(e?.response?.data?.error || 'Erro ao salvar o setor/finalização da OS.')
    } finally {
      setSalvandoFinalizacaoOS(false)
    }
  }

  function selecionarOS(o: any) {
    setItemForm(f => ({
      ...f,
      ordemServicoCod: String(o.ordnum), ordemServicoLinha: String(o.ordlin), ordemServicoDes: o.orddes,
      tipoServicoCod: String(o.ordser_cod), tipoServicoDes: o.orddes,
      osExecutada: o.ordexe === 'E',
    }))
    setModalOSAberto(false)
  }

  async function carregarItemDaOrdemCompra() {
    setAvisoOrdemItem('')
    if (!itemForm.ordemCompraNum || !itemForm.ordemCompraLinha) return
    const est = empresaCod ?? 1
    setCarregandoItemOrdem(true)
    try {
      const r = await api.get(`/erp/ordem-compra/${est}/${itemForm.ordemCompraNum}`)
      const item = (r.data?.itens || []).find((it: any) => String(it.linha) === itemForm.ordemCompraLinha)
      if (!item) { setAvisoOrdemItem('Linha não encontrada nesta ordem de compra'); return }
      setItemForm(f => ({
        ...f,
        itemCod: item.itemCod, itemDes: item.itemDes, itemUn: item.unidade,
        quantidade: item.quantidade, valorTotal: item.valor,
        destinoCod: item.destCompraCod, destinoDes: item.destCompraDes,
        veiculoCod: item.veiculoCod || f.veiculoCod, veiculoDes: item.veiculoCod || f.veiculoDes,
      }))
    } catch {
      setAvisoOrdemItem('Não foi possível carregar esta ordem de compra')
    } finally {
      setCarregandoItemOrdem(false)
    }
  }

  function abrirNovoItem() {
    // Item nasce sem Natureza/CFOP: o usuário digita a de cada item (não herda da capa).
    const base = {
      ...ITEM_VAZIO, ordemCompraNum: ordemCompra || '', hora: horaAtualStr(),
    }
    // Tem item importado do XML esperando na fila? Carrega ele em vez de abrir em
    // branco — senão a importação preenchia tudo e o "Adicionar Item" jogava fora.
    const importado = itensImportadosXml?.[idxItemImportado]
    // NFS-e que ainda não fechou o total: abre de novo o serviço do XML com o valor que falta
    const restanteNfse = !importado ? itemRestanteNfse(itens) : null
    if (restanteNfse) {
      setItemForm(restanteNfse)
      setItemFormTravadoXml(true)
      setItemEditandoIdx(null)
      setTabItem('geral')
      setErroItem('')
      setModalItemAberto(true)
      return
    }
    setItemForm(importado ? {
      ...importado,
      ordemCompraNum: importado.ordemCompraNum || base.ordemCompraNum,
    } : base)
    setItemFormTravadoXml(!!importado)
    setItemEditandoIdx(null)
    setTabItem('geral')
    setErroItem('')
    setModalItemAberto(true)
  }

  function abrirEditarItem(idx: number) {
    setItemForm(itens[idx])
    setItemFormTravadoXml(false)
    setItemEditandoIdx(idx)
    setTabItem('geral')
    setErroItem('')
    setModalItemAberto(true)
  }

  function salvarItem(concluirDepois = false) {
    if (!itemForm.itemCod) { setErroItem('Escolha o item do estoque antes de adicionar!'); return }
    if (modDoctoFiscal === '55' && !itemForm.ncmCod) { setErroItem('Informe o NCM do item!'); return }
    // Destino obrigatório e compatível com o tipo do item (regra da PCCO019: produto
    // aceita Consumo ou Armazenagem, serviço só aceita Consumo).
    if (!itemForm.destinoCod) { setErroItem('Informe o Destino!'); return }
    if (itemForm.itemTipo === 'S') {
      if (itemForm.destinoTipo !== 3) { setErroItem('Destino do Serviço deve ser 3-Consumo!'); return }
    } else if (itemForm.destinoTipo !== 3 && itemForm.destinoTipo !== 5) {
      setErroItem('Destino da Mercadoria deve ser 3-Consumo ou 5-Armazenagem!')
      return
    }
    // Conta Estoque x Aplicação são mutuamente exclusivos conforme o Destino (regra fixa).
    if (itemForm.destinoTipo === 5 && !itemForm.contaEstoqueCod) {
      setErroItem('Informe a Conta Estoque (obrigatória quando o Destino é Estoque)!')
      return
    }
    // Fora do Estoque a Conta Aplicação é obrigatória (no rateio por centro de custo ela vem em cada linha).
    if (itemForm.destinoTipo !== 5 && itemForm.rateioCusto !== 'C' && !itemForm.aplicacaoCod) {
      setErroItem('Informe a Conta Aplicação (obrigatória quando o Destino não é Estoque)!')
      return
    }
    // Validações do rateio de custo (Pnv_TipCcu), conforme regras da PCCO019.
    if (itemForm.rateioCusto === 'P') {
      if (!itemForm.numeroParcelas) { setErroItem('Informe o número de parcelas do rateio!'); return }
      if (Number(itemForm.numeroParcelas) <= 1) { setErroItem('Para realizar rateio o número de parcelas deve ser maior que 1!'); return }
    }
    if (itemForm.rateioCusto && (!itemForm.centroCustoCod || itemForm.destinoTipo === 5)) {
      setErroItem('Tipo do Rateio deve ser Nenhum (falta Centro de Custo ou o destino é Estoque)')
      return
    }
    if (itemForm.rateioCusto === 'C') {
      if (itemForm.rateioLinhas.length < 2) { setErroItem('Rateio por Centro de Custo precisa de pelo menos 2 contas — adicione as linhas ou mude o Tipo do Rateio pra Nenhum.'); return }
      if (itemForm.rateioLinhas.some(l => !l.centroCustoCod || !l.aplicacaoCod)) { setErroItem('Toda linha do rateio precisa de Centro de Custo e Conta Aplicação.'); return }
      const totalRateado = itemForm.rateioLinhas.reduce((s, l) => s + parseNumBr(l.valor), 0)
      if (Math.abs(totalRateado - parseNumBr(itemForm.valorTotal)) >= 0.01) {
        setErroItem(`A soma do rateio (R$ ${fmtMoeda(String(totalRateado))}) precisa bater com o valor do item (R$ ${itemForm.valorTotal}).`)
        return
      }
    }
    if (!notaSalva) { setErroItem('Confirme a capa da nota antes de lançar itens.'); return }
    if (!itemForm.naturezaCod) { setErroItem('Informe a Natureza Operação / CFOP do item!'); return }
    if (nfseXml) {
      const outros = itens.filter((_, i) => i !== itemEditandoIdx)
      const valor = parseNumBr(itemForm.valorTotal)
      if (valor <= 0) { setErroItem('Informe o valor do item!'); return }
      if (somaValorItens(outros) + valor > nfseXml.total + 0.005) {
        const resta = Math.max(0, nfseXml.total - somaValorItens(outros))
        setErroItem(`O valor passa do total da NFS-e (R$ ${fmtMoeda(String(nfseXml.total))}) — sobram só R$ ${fmtMoeda(String(resta))} pra este item.`)
        return
      }
    }
    // Validações da Ordem de Serviço (só quando o destino exige veículo).
    if (itemForm.destinoVeiSempre) {
      if (itemForm.osAutomatica === 'G' && !itemForm.tipoServicoCod) { setErroItem('Informe o Tipo de Serviço pra gerar a OS!'); return }
      if (itemForm.osAutomatica === 'V' && !itemForm.ordemServicoCod) { setErroItem('Escolha a Ordem de Serviço pra vincular!'); return }
      if (itemForm.osAutomatica === 'I' && (!itemForm.ordemServicoCod || !itemForm.tipoServicoCod)) {
        setErroItem('Informe o número da OS e o Tipo de Serviço!'); return
      }
    }
    setErroItem('')

    const payload = {
      itemCod: Number(itemForm.itemCod) || 0,
      itemDes: itemForm.itemDes,
      descComplementar: itemForm.descComplementar,
      itemUn: itemForm.itemUn,
      itemTipo: itemForm.itemTipo,
      quantidade: parseNumBr(itemForm.quantidade),
      valorTotal: parseNumBr(itemForm.valorTotal),
      valorUnitario: itemForm.valorUnitario ? parseNumBr(itemForm.valorUnitario) : undefined,
      origemCod: itemForm.origemCod,
      destinoCod: itemForm.destinoCod,
      veiculoCod: itemForm.veiculoCod,
      hodometro: itemForm.hodometro ? Number(itemForm.hodometro) : null,
      hora: Number(itemForm.hora) || 12,
      pneuCod: itemForm.pneuCod ? Number(itemForm.pneuCod) : null,
      recapagem: itemForm.recapagem,
      postoCod: itemForm.postoCod,
      tipoCombustivel: itemForm.tipoCombustivel,
      osAutomatica: itemForm.osAutomatica,
      ordemServicoCod: itemForm.ordemServicoCod ? Number(itemForm.ordemServicoCod) : null,
      ordemServicoLinha: itemForm.ordemServicoLinha ? Number(itemForm.ordemServicoLinha) : null,
      tipoServicoCod: itemForm.tipoServicoCod ? Number(itemForm.tipoServicoCod) : null,
      aplicacaoCod: itemForm.aplicacaoCod,
      contaEstoqueCod: itemForm.contaEstoqueCod ? Number(itemForm.contaEstoqueCod) : null,
      centroCustoCod: itemForm.centroCustoCod,
      rateioCusto: itemForm.rateioCusto,
      numeroParcelas: itemForm.numeroParcelas ? Number(itemForm.numeroParcelas) : null,
      rateioLinhas: itemForm.rateioCusto === 'C'
        ? itemForm.rateioLinhas.map(l => ({
            centroCustoCod: l.centroCustoCod, aplicacaoCod: l.aplicacaoCod,
            percentual: parseNumBr(l.percentual), valor: parseNumBr(l.valor),
          }))
        : undefined,
      ncmCod: itemForm.ncmCod,
      // NFS-e: pnv_servi guarda o NBS (orientação do Leandro); nos demais, o tipo de serviço Reinf.
      servicoCod: modDoctoFiscal === '99' ? itemForm.nbsCod : itemForm.servicoCod,
      itemValorFrete: parseNumBr(itemForm.itemValorFrete),
      itemValorAcrescimo: parseNumBr(itemForm.itemValorAcrescimo),
      itemValorSeguro: parseNumBr(itemForm.itemValorSeguro),
      itemValorDesconto: parseNumBr(itemForm.itemValorDesconto),
      inssBase: parseNumBr(itemForm.inssBase), inssPerc: parseNumBr(itemForm.inssPerc), inssValor: parseNumBr(itemForm.inssValor),
      icmsBase: parseNumBr(itemForm.icmsBase), icmsPerc: parseNumBr(itemForm.icmsPerc), icmsValor: parseNumBr(itemForm.icmsValor), icmsCst: itemForm.icmsCst,
      icmsStBase: parseNumBr(itemForm.icmsStBase), icmsStPerc: parseNumBr(itemForm.icmsStPerc), icmsStValor: parseNumBr(itemForm.icmsStValor),
      ipiBase: parseNumBr(itemForm.ipiBase), ipiPerc: parseNumBr(itemForm.ipiPerc), ipiValor: parseNumBr(itemForm.ipiValor), ipiCst: itemForm.ipiCst,
      pisBase: parseNumBr(itemForm.pisBase), pisPerc: parseNumBr(itemForm.pisPerc), pisValor: parseNumBr(itemForm.pisValor), pisCst: itemForm.pisCst,
      cofinsBase: parseNumBr(itemForm.cofinsBase), cofinsPerc: parseNumBr(itemForm.cofinsPerc), cofinsValor: parseNumBr(itemForm.cofinsValor), cofinsCst: itemForm.cofinsCst,
      difal: parseNumBr(itemForm.difal),
      ordemCompraNum: itemForm.ordemCompraNum ? Number(itemForm.ordemCompraNum) : null,
      ordemCompraLinha: itemForm.ordemCompraLinha ? Number(itemForm.ordemCompraLinha) : null,
      tnocod: Number(itemForm.naturezaCod) || 0,
      ibsCbsCst: itemForm.ibsCbsCst, ibsCbsClassTrib: itemForm.ibsCbsClassTrib,
      cbsBase: parseNumBr(itemForm.cbsBase), cbsBcEf: parseNumBr(itemForm.cbsBcEf), cbsAliRef: parseNumBr(itemForm.cbsAliRef),
      cbsRedAli: parseNumBr(itemForm.cbsRedAli), cbsAliEf: parseNumBr(itemForm.cbsAliEf), cbsValor: parseNumBr(itemForm.cbsValor),
      ibsUfEst: itemForm.ibsUfEst, ibsUfBcEf: parseNumBr(itemForm.ibsUfBcEf), ibsUfAliRef: parseNumBr(itemForm.ibsUfAliRef),
      ibsUfRedAli: parseNumBr(itemForm.ibsUfRedAli), ibsUfAliEf: parseNumBr(itemForm.ibsUfAliEf), ibsUfValor: parseNumBr(itemForm.ibsUfValor),
      ibsMunBase: parseNumBr(itemForm.ibsMunBase), ibsMunBcEf: parseNumBr(itemForm.ibsMunBcEf), ibsMunAliRef: parseNumBr(itemForm.ibsMunAliRef),
      ibsMunRedAli: parseNumBr(itemForm.ibsMunRedAli), ibsMunAliEf: parseNumBr(itemForm.ibsMunAliEf), ibsMunValor: parseNumBr(itemForm.ibsMunValor),
      isBase: parseNumBr(itemForm.isBase), isAliq: parseNumBr(itemForm.isAliq),
      ibsCbsMono: itemForm.ibsCbsMono,
      pisXmlBase: parseNumBr(itemForm.pisXmlBase), pisXmlAliq: parseNumBr(itemForm.pisXmlAliq), pisXmlValor: parseNumBr(itemForm.pisXmlValor), pisXmlCst: itemForm.pisXmlCst,
      cofinsXmlBase: parseNumBr(itemForm.cofinsXmlBase), cofinsXmlAliq: parseNumBr(itemForm.cofinsXmlAliq), cofinsXmlValor: parseNumBr(itemForm.cofinsXmlValor), cofinsXmlCst: itemForm.cofinsXmlCst,
    }
    // Série em branco vai como %20 (senão a URL fica com "//" e dá 404)
    const base = `/erp/notas-entrada/${notaSalva.empcod}/${encodeURIComponent(notaSalva.serie.trim() || ' ')}/${notaSalva.numero}/${fornecedorCod}/itens`

    setSalvandoItem(true)
    ;(async () => {
      try {
        // Resolve a Ordem de Serviço ANTES de gravar o item — "Gerar OS/Serviço" cria
        // a OS agora (ainda não existe número nenhum) e "Incluir Serviço" grava a linha
        // na OS já existente (avisando, sem bloquear, se o serviço já estiver lá —
        // mesmo comportamento do legado WCORDSE1). "Vincular à OS" já veio pronto do
        // modal de busca, não precisa resolver nada aqui.
        let osParaFinalizar: { ordnum: number; ordlin: number } | null = null
        if (itemForm.destinoVeiSempre && itemForm.osAutomatica === 'G') {
          const r = await api.post('/erp/ordem-servico/criar', {
            empcod: notaSalva.empcod, veiculoCod: itemForm.veiculoCod,
            hodometro: itemForm.hodometro ? Number(itemForm.hodometro) : 0,
            hora: Number(itemForm.hora) || 12, dataEmissao,
            servicoCod: Number(itemForm.tipoServicoCod), servicoDes: itemForm.tipoServicoDes,
          })
          payload.ordemServicoCod = r.data.ordnum
          payload.ordemServicoLinha = r.data.ordlin
          osParaFinalizar = { ordnum: r.data.ordnum, ordlin: r.data.ordlin }
        } else if (itemForm.destinoVeiSempre && itemForm.osAutomatica === 'I') {
          const ordnum = Number(itemForm.ordemServicoCod)
          let forcar = false
          for (;;) {
            const r = await api.post(`/erp/ordem-servico/${ordnum}/incluir-servico`, {
              veiculoCod: itemForm.veiculoCod, hodometro: itemForm.hodometro ? Number(itemForm.hodometro) : 0,
              dataEmissao, servicoCod: Number(itemForm.tipoServicoCod), servicoDes: itemForm.tipoServicoDes, forcar,
            })
            if (r.data.duplicado) {
              if (!window.confirm('Este serviço já está cadastrado nessa OS. Deseja continuar mesmo assim?')) {
                setSalvandoItem(false)
                return
              }
              forcar = true
              continue
            }
            payload.ordemServicoCod = ordnum
            payload.ordemServicoLinha = r.data.ordlin
            if (r.data.ordexeAtual !== 'E') osParaFinalizar = { ordnum, ordlin: r.data.ordlin }
            break
          }
        } else if (itemForm.destinoVeiSempre && itemForm.osAutomatica === 'V' && !itemForm.osExecutada) {
          osParaFinalizar = { ordnum: Number(itemForm.ordemServicoCod), ordlin: Number(itemForm.ordemServicoLinha) }
        }

        if (itemEditandoIdx !== null) {
          const linha = itens[itemEditandoIdx].linha
          await api.put(`${base}/${linha}`, payload)
          setItens(list => list.map((it, i) => i === itemEditandoIdx ? { ...itemForm, linha, ordemServicoCod: String(payload.ordemServicoCod ?? ''), ordemServicoLinha: String(payload.ordemServicoLinha ?? '') } : it))
          // Reabre a tela de Recapagem/Conserto também na edição — ex: usuário
          // marcou Conserto por engano, volta no item e troca pra Recapagem.
          if (itemForm.pneuCod) {
            enfileirarRecapagem({
              pnvLin: Number(linha),
              pnucod: Number(itemForm.pneuCod),
              procedimento: itemForm.recapagem === 'S' ? 'S' : 'N',
              valorInicial: parseNumBr(itemForm.valorTotal),
            })
          }
        } else {
          const r = await api.post(base, payload)
          setItens(list => [...list, { ...itemForm, linha: r.data.linha, ordemServicoCod: String(payload.ordemServicoCod ?? ''), ordemServicoLinha: String(payload.ordemServicoLinha ?? '') }])
          if (itemForm.pneuCod) {
            enfileirarRecapagem({
              pnvLin: r.data.linha,
              pnucod: Number(itemForm.pneuCod),
              procedimento: itemForm.recapagem === 'S' ? 'S' : 'N',
              valorInicial: parseNumBr(itemForm.valorTotal),
            })
          }
        }
        // NFS-e com valor faltando: já abre o próximo item com o restante (mesmo serviço do XML)
        const lancados = itemEditandoIdx !== null
          ? itens.map((it, i) => i === itemEditandoIdx ? itemForm : it)
          : [...itens, itemForm]
        const proximoNfse = itemEditandoIdx === null ? itemRestanteNfse(lancados, itemForm) : null
        if (proximoNfse) {
          if (itensImportadosXml) setItensImportadosXml(null)
          setItemForm(proximoNfse)
          setItemFormTravadoXml(true)
        // Se tem mais item importado do XML na fila, já carrega o próximo e
        // mantém o modal aberto — senão fecha normalmente.
        } else if (itensImportadosXml && idxItemImportado + 1 < itensImportadosXml.length) {
          const prox = idxItemImportado + 1
          setItemForm(itensImportadosXml[prox])
          setItemFormTravadoXml(true)
          setIdxItemImportado(prox)
        } else {
          if (itensImportadosXml) setItensImportadosXml(null)
          setItemFormTravadoXml(false)
          setModalItemAberto(false)
          if (concluirDepois) setConcluirAgora(true)
        }
        if (osParaFinalizar) abrirFinalizarOS(osParaFinalizar.ordnum, osParaFinalizar.ordlin)
      } catch (e: any) {
        setErroItem(e?.response?.data?.error || 'Erro ao salvar item da nota')
      } finally {
        setSalvandoItem(false)
      }
    })()
  }

  async function removerItem(idx: number) {
    if (!window.confirm('Remover este item da nota?')) return
    const item = itens[idx]
    try {
      if (notaSalva && item.linha) {
        await api.delete(`/erp/notas-entrada/${notaSalva.empcod}/${encodeURIComponent(notaSalva.serie.trim() || ' ')}/${notaSalva.numero}/${fornecedorCod}/itens/${item.linha}`)
      }
      setItens(list => list.filter((_, i) => i !== idx))
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Erro ao remover item da nota')
    }
  }

  // Regra pedida pelo usuário: fornecedor do mesmo Estado da empresa só pode usar
  // CFOP que começa com 1 (operação estadual); de outro Estado, só CFOP começando
  // com 2 (interestadual). Só filtra quando dá pra saber os dois Estados.
  const foraDoEstadoFornecedor = !!fornecedorUf && !!empresaAtual?.empest && fornecedorUf !== empresaAtual.empest
  const prefixoCfopEsperado = fornecedorUf && empresaAtual?.empest ? (foraDoEstadoFornecedor ? '2' : '1') : ''
  const naturezasParaSelecionar = prefixoCfopEsperado
    ? naturezas.filter(n => String(n.tno_cdf).charAt(0) === prefixoCfopEsperado)
    : naturezas

  return (
    <div style={{ maxWidth: 1040 }}>
      <div className="card" style={{ padding: '1.1rem 1.3rem' }}>
        <h3 style={{
          fontSize: '0.98rem', fontWeight: 700, marginBottom: '0.8rem', color: '#1a6ea8',
          paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff',
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        }}>
          Lançamento Notas
          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#111827' }}>TNOTFIS</span>
        </h3>

        <Secao titulo="Cabeçalho">
          <Campo label="Estab." width={55}>
            <select value={empresaCod ?? ''} onChange={e => setEmpresaCod(Number(e.target.value))}>
              {empresas.map(e => <option key={e.empcod} value={e.empcod}>{e.empcod}</option>)}
            </select>
          </Campo>
          <Campo label="Nome do Estabelecimento" width={190}>
            <input type="text" value={empresaAtual?.empfan || empresaAtual?.empnom || ''} readOnly disabled />
          </Campo>
          <Campo label="Série" width={70}>
            <input type="text" value={serie} readOnly={travadoPorXml} style={travadoPorXml ? estiloXml : undefined}
              onChange={e => setSerie(e.target.value)} />
          </Campo>
          <Campo label="Número Nota" width={100}>
            <input
              type="text" inputMode="numeric" value={numeroNota} readOnly={travadoPorXml}
              style={travadoPorXml ? estiloXml : undefined}
              onChange={e => {
                const v = e.target.value.replace(/\D/g, '')
                setNumeroNota(v)
                if (!nrFaturaEditada) setNrFatura(v)
              }}
            />
          </Campo>
          <div style={{ width: '100%' }} />
          <Campo label="CNPJ Fornecedor" width={380}>
            <LookupSelect
              endpoint="/erp/clientes/lista"
              codKey="cli_cod" descKey="cli_fan"
              renderItem={(item: any) => (
                <>
                  <div className="nat-item-linha1">
                    <span className="nat-item-cod">{item.cli_mat}</span>
                    {item.bloqueado && (
                      <span style={{ marginLeft: 6, fontSize: '0.65rem', fontWeight: 700, color: '#fff', background: '#dc2626', borderRadius: 4, padding: '1px 6px' }}>
                        BLOQUEADO
                      </span>
                    )}
                  </div>
                  <div className="nat-item-dsc">{item.cli_fan || item.cli_nom}</div>
                </>
              )}
              codValue={fornecedorCod} descValue={fornecedorDes}
              valorExibido={fornecedorDes ? `${fmtCnpj(fornecedorCnpj)} - ${fornecedorDes}` : undefined}
              onSelect={onSelecionarFornecedor}
              placeholder="Buscar fornecedor..." listWidth={320} somenteComTexto
            />
          </Campo>
          {fornecedorCod && (fornecedorCidade || fornecedorUf) && (
            <Campo label="Endereço do Fornecedor" width={190}>
              {(() => {
                const foraDoEstado = !!fornecedorUf && !!empresaAtual?.empest && fornecedorUf !== empresaAtual.empest
                return (
                  <div style={{
                    display: 'flex', alignItems: 'center', height: 30, fontSize: '0.82rem', fontWeight: 600,
                    color: foraDoEstado ? '#c2410c' : '#374151',
                  }} title={foraDoEstado ? 'Fornecedor de outro Estado — CFOP deve começar com 2' : undefined}>
                    {fornecedorCidade}{fornecedorCidade && fornecedorUf ? ' - ' : ''}{fornecedorUf}
                  </div>
                )
              })()}
            </Campo>
          )}
          <Campo label=" " width={190}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={abrirOrdensVinculadas}
              style={{ height: 30 }} title="Ver ordens de compra em aberto deste fornecedor (últimos 30 dias)">
              Ordens deste Fornecedor
            </button>
          </Campo>
          {avisoOrdensVinculadas && (
            <div style={{ width: '100%', color: '#dc2626', fontSize: '0.75rem', fontWeight: 600 }}>
              ⚠ {avisoOrdensVinculadas}
            </div>
          )}
          {fornecedorBloqueado && (
            <div style={{
              width: '100%', background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626',
              borderRadius: 6, padding: '0.5rem 0.8rem', fontSize: '0.8rem', fontWeight: 600,
            }}>
              ⚠ Este fornecedor está BLOQUEADO — não é possível lançar nota para ele.
            </div>
          )}
        </Secao>

        <Secao titulo="Datas e Referências">
          <Campo label="Data de Emissão" width={130}>
            <input
              ref={dataEmissaoInputRef}
              type="date" value={dataEmissao} readOnly={travadoPorXml}
              onChange={e => {
                if (travadoPorXml) return
                const v = limitarAno4Digitos(dataEmissao, e.target.value)
                setDataEmissao(v)
                if (!v) return
                setAvisoDataEmissao(v > hojeISO() ? 'Data de emissão não pode ser maior que hoje!' : '')
              }}
              onKeyDown={e => {
                if (e.key === 'Tab' && !e.shiftKey && !dataEmissao) {
                  e.preventDefault()
                  setAvisoDataEmissao('A data de Emissão deve ser Informada')
                }
              }}
              required
              style={avisoDataEmissao || avisoPeriodoEmissao ? { outline: '2px solid #dc2626', borderColor: '#dc2626' } : travadoPorXml ? estiloXml : undefined}
            />
          </Campo>
          <Campo label="Data Recebimento" width={130}>
            <input
              type="date" value={dataRecebimento}
              onChange={e => {
                const v = limitarAno4Digitos(dataRecebimento, e.target.value)
                setDataRecebimento(v)
                if (!v) return
                setAvisoDataRecebimento(
                  v > hojeISO() ? 'Data de recebimento não pode ser maior que hoje!'
                  : dataEmissao && v < dataEmissao ? 'Data de recebimento anterior à data de emissão da NF!'
                  : ''
                )
              }}
              onKeyDown={e => {
                if (e.key === 'Tab' && !e.shiftKey) {
                  if (!dataRecebimento) { e.preventDefault(); setAvisoDataRecebimento('A Data de Recebimento deve ser Informada') }
                  else if (dataEmissao && dataRecebimento < dataEmissao) { e.preventDefault(); setAvisoDataRecebimento('Data de recebimento anterior à data de emissão da NF!') }
                }
              }}
              required
              style={avisoDataRecebimento || avisoPeriodoRecebimento ? { outline: '2px solid #dc2626', borderColor: '#dc2626' } : undefined}
            />
          </Campo>
          {(avisoDataEmissao || avisoDataRecebimento || avisoPeriodoEmissao || avisoPeriodoRecebimento) && (
            <div style={{
              width: '100%', color: '#dc2626', fontSize: '0.75rem', fontWeight: 600,
            }}>
              {avisoDataEmissao && <div>⚠ {avisoDataEmissao}</div>}
              {avisoPeriodoEmissao && <div>⚠ {avisoPeriodoEmissao}</div>}
              {avisoDataRecebimento && <div>⚠ {avisoDataRecebimento}</div>}
              {avisoPeriodoRecebimento && <div>⚠ {avisoPeriodoRecebimento}</div>}
            </div>
          )}
          <Campo label="Ordem Compra" width={100}>
            <input
              type="text" inputMode="numeric" value={ordemCompra}
              onChange={e => setOrdemCompra(e.target.value.replace(/\D/g, ''))}
              style={{ background: '#eff6ff', borderColor: '#93c5fd', color: '#1a6ea8', fontWeight: 600 }}
            />
          </Campo>
          <Campo label="Código Comprador" width={220}>
            <LookupSelect
              endpoint="/compradores" extraParams={SOMENTE_ATIVOS}
              codKey="cpr_cod" descKey="cpr_des"
              codValue={compradorCod} descValue={compradorDes}
              onSelect={(cod, des) => { setCompradorCod(cod); setCompradorDes(des) }}
              placeholder="Buscar comprador..." listWidth={260}
            />
          </Campo>
        </Secao>

        <Secao titulo="Finalidade da NF">
          <Campo label="Finalidade da NF" width={340}>
            <select value={finalidadeNf} onChange={e => setFinalidadeNf(e.target.value)}>
              <option value="COM">COM - NF de Compra/Aquisição de Produtos ou Serviços</option>
              <option value="TRA">TRA - NF de Transferência</option>
              <option value="DEV">DEV - NF de Devolução</option>
              <option value="OUT">OUT - Outras Finalidades</option>
            </select>
          </Campo>
          <Campo label="Modelo Fiscal" width={90}>
            {travadoPorXml || modeloFixo ? (
              <input type="text" value={`${modDoctoFiscal} - ${modDoctoFiscalDes}`} readOnly style={travadoPorXml ? estiloXml : undefined} />
            ) : (
            <LookupSelect
              endpoint="/erp/moddoc/lista"
              codKey="moddoc_cod" descKey="moddoc_des" tagKey="moddoc_cod"
              codValue={modDoctoFiscal} descValue={modDoctoFiscalDes} valorExibido={modDoctoFiscal}
              onSelect={async (cod, des, item) => {
                if (cod === '99') { navigate(ROTA_LANCAMENTO_NFSE); return }
                setModDoctoFiscal(cod); setModDoctoFiscalDes(des)
                const sigla = String(item?.moddoc_dfe || '').trim()
                if (sigla && !tipoDocumento) {
                  try {
                    const r = await api.get('/erp/doccad/lista', { params: { busca: sigla } })
                    const encontrado = (r.data || []).find((d: any) => d.doc_cod === sigla)
                    if (encontrado) { setTipoDocumento(encontrado.doc_cod); setTipoDocumentoDes(encontrado.doc_des) }
                  } catch { /* autofill é best-effort, usuário preenche manual se falhar */ }
                }
              }}
              placeholder="Modelo..." listWidth={380}
            />
            )}
          </Campo>
          <Campo label="Chave NF-e / NFS-e" width={430}>
            <input
              type="text" value={chaveNfe} onChange={e => setChaveNfe(e.target.value)} maxLength={50}
              readOnly={travadoPorXml}
              style={{
                fontFamily: 'monospace', fontSize: '0.8rem',
                ...(travadoPorXml ? estiloXml : { background: '#eff6ff', borderColor: '#93c5fd', color: '#1a6ea8', fontWeight: 600 }),
              }}
            />
          </Campo>
          {!travadoPorXml && (
            <Campo label=" " width={190}>
              <button
                type="button" className="btn btn-secondary btn-sm"
                disabled={importandoXml || ![44, 50].includes(chaveNfe.replace(/\D/g, '').length)}
                title="Preenche a nota e os itens a partir do XML já baixado na Distribuição DF-e (44 dígitos) ou do Manifestador de NFS-e (50 dígitos)"
                onClick={() => importarDoXml()}
              >
                {importandoXml ? 'Importando...' : '📥 Importar do XML'}
              </button>
            </Campo>
          )}
          {avisoChaveCnpj && (
            <div style={{ width: '100%', color: '#dc2626', fontSize: '0.75rem', fontWeight: 600 }}>⚠ {avisoChaveCnpj}</div>
          )}
          {avisoImportarXml && (
            <div style={{ width: '100%', color: '#b45309', fontSize: '0.75rem', fontWeight: 600 }}>⚠ {avisoImportarXml}</div>
          )}
          {fornecedorPendenteXml && (
            <div style={{
              width: '100%', background: '#fffbeb', border: '1px solid #fbbf24', color: '#92400e',
              borderRadius: 6, padding: '0.6rem 0.9rem', fontSize: '0.8rem', display: 'flex',
              alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap',
            }}>
              <span>
                ⚠ Fornecedor <strong>{fornecedorPendenteXml.cliNom}</strong> (CNPJ {fornecedorPendenteXml.cliMat}) não está cadastrado.
                Deseja cadastrar automaticamente com os dados do XML?
              </span>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button type="button" className="btn btn-primary btn-sm" onClick={confirmarCadastroFornecedorXml} disabled={importandoXml}>
                  {importandoXml ? 'Cadastrando...' : 'Sim, cadastrar'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={recusarCadastroFornecedorXml} disabled={importandoXml}>
                  Não
                </button>
              </div>
            </div>
          )}
          <Campo label="Natureza Operação" width={640}>
            <NaturezaSelect naturezas={naturezasParaSelecionar} value={naturezaOpCod1} onChange={onSelecionarNatureza} />
          </Campo>
          {prefixoCfopEsperado && (
            <div style={{ width: '100%', color: foraDoEstadoFornecedor ? '#c2410c' : '#1a6ea8', fontSize: '0.75rem', fontWeight: 600 }}>
              ℹ Fornecedor {foraDoEstadoFornecedor ? `de outro Estado (${fornecedorUf})` : `do mesmo Estado (${fornecedorUf})`} da
              empresa ({empresaAtual!.empest}) — só listando naturezas com CFOP começando em {prefixoCfopEsperado}.
            </div>
          )}
          {(() => {
            if (!naturezaOpCod2 || !fornecedorUf || !empresaAtual?.empest) return null
            const foraDoEstado = fornecedorUf !== empresaAtual.empest
            const primeiroDigito = naturezaOpCod2.charAt(0)
            const cfopDivergente = foraDoEstado ? primeiroDigito !== '2' : primeiroDigito === '2'
            if (!cfopDivergente) return null
            return (
              <div style={{
                width: '100%', background: '#fff7ed', border: '1px solid #fdba74', color: '#c2410c',
                borderRadius: 6, padding: '0.5rem 0.8rem', fontSize: '0.8rem', fontWeight: 600,
              }}>
                ⚠ Fornecedor é {foraDoEstado ? `de outro Estado (${fornecedorUf})` : `do mesmo Estado (${fornecedorUf})`} da
                empresa ({empresaAtual.empest}), mas a CFOP {naturezaOpCod2} começa com {primeiroDigito} —
                o esperado seria começar com {foraDoEstado ? '2' : '1'}.
              </div>
            )
          })()}
          {finalidadeNf === 'DEV' && (
            <>
              <Campo label="Nº NF Original" width={110}>
                <input type="text" inputMode="numeric" value={nfOriginalNumero} onChange={e => setNfOriginalNumero(e.target.value.replace(/\D/g, ''))} />
              </Campo>
              <Campo label="Série NF Original" width={90}>
                <input type="text" value={nfOriginalSerie} onChange={e => setNfOriginalSerie(e.target.value)} />
              </Campo>
            </>
          )}
        </Secao>

        <Secao titulo="Documento e Financeiro">
          <Campo label="Tipo Documento" width={90}>
            <LookupSelect
              endpoint="/erp/doccad/lista"
              codKey="doc_cod" descKey="doc_des" tagKey="doc_cod"
              codValue={tipoDocumento} descValue={tipoDocumento}
              onSelect={(cod, des) => { setTipoDocumento(cod); setTipoDocumentoDes(des) }}
              placeholder="Tipo..." listWidth={260}
            />
          </Campo>
          <Campo label="Nr Fatura" width={120}>
            <input type="text" value={nrFatura} onChange={e => { setNrFatura(e.target.value); setNrFaturaEditada(true) }} />
          </Campo>
          <div style={{ width: '100%' }} />
          <Campo label="Conta Financeira" width={290}>
            <ContaSelect value={contaFinanceira} onChange={(cod, des) => { setContaFinanceira(cod); setContaFinanceiraDes(des) }} />
          </Campo>
          <Campo label="Condição Pagamento" width={290}>
            <LookupSelect
              endpoint="/condicao-pagamento"
              codKey="tcv_cod" descKey="tcv_des" tagKey="tcv_cod"
              codValue={condicaoPagamento} descValue={condicaoPagamentoDes}
              onSelect={(cod, des) => { setCondicaoPagamento(cod); setCondicaoPagamentoDes(des) }}
              placeholder="Buscar condição de pagamento..." listWidth={320}
            />
          </Campo>
        </Secao>

        <Secao titulo="Desconto e Frete" colapsavel>
          <Campo label="Tipo de Frete" width={260}>
            <select value={tipoFrete} onChange={e => setTipoFrete(e.target.value)}>
              <option value="9">9-Sem Ocorrência de Transporte</option>
              <option value="0">0-Pago pelo Emitente</option>
              <option value="1">1-Pago pelo Dest./Remetente</option>
              <option value="2">2-Pago por Terceiros</option>
            </select>
          </Campo>
          {tipoFrete !== '9' && (
            <Campo label="Valor Frete" width={100}>
              <input type="text" inputMode="decimal" value={valorFrete} readOnly={travadoPorXml} style={travadoPorXml ? estiloXml : undefined} onChange={e => setValorFrete(e.target.value)} />
            </Campo>
          )}
          <Campo label="R$ Seguro" width={100}>
            <input type="text" inputMode="decimal" value={valorSeguro} readOnly={travadoPorXml} style={travadoPorXml ? estiloXml : undefined} onChange={e => setValorSeguro(e.target.value)} />
          </Campo>
          <Campo label="Valor Desconto R$" width={100}>
            <input type="text" inputMode="decimal" value={valorDescontoRs} readOnly={travadoPorXml} style={travadoPorXml ? estiloXml : undefined} onChange={e => setValorDescontoRs(e.target.value)} />
          </Campo>
          <Campo label="R$ Acréscimo" width={100}>
            <input type="text" inputMode="decimal" value={valorAcrescimo} readOnly={travadoPorXml} style={travadoPorXml ? estiloXml : undefined} onChange={e => setValorAcrescimo(e.target.value)} />
          </Campo>
        </Secao>

        <Secao titulo="Impostos Retidos" colapsavel>
          <Campo label="Valor do IRRF" width={110}>
            <input type="text" inputMode="decimal" value={valorIrrf} readOnly={travadoPorXml} style={travadoPorXml ? estiloXml : undefined} onChange={e => setValorIrrf(e.target.value)} />
          </Campo>
          <Campo label="% IRRF" width={80}>
            <input type="text" inputMode="decimal" value={percIrrf} onChange={e => setPercIrrf(e.target.value)} />
          </Campo>
          <Campo label="Valor INSS" width={110}>
            <input type="text" inputMode="decimal" value={valorInss} readOnly={travadoPorXml} style={travadoPorXml ? estiloXml : undefined} onChange={e => setValorInss(e.target.value)} />
          </Campo>
          <Campo label="Val ISSQN" width={110}>
            <input type="text" inputMode="decimal" value={valorIssqn} onChange={e => setValorIssqn(e.target.value)} />
          </Campo>
          <Campo label="% ISS" width={80}>
            <input type="text" inputMode="decimal" value={percIss} onChange={e => setPercIss(e.target.value)} />
          </Campo>
          {fornecedorFj !== 'F' && (
            <>
              <Campo label="Valor PIS" width={110}>
                <input type="text" inputMode="decimal" value={valorPis} readOnly={travadoPorXml} style={travadoPorXml ? estiloXml : undefined} onChange={e => setValorPis(e.target.value)} />
              </Campo>
              <Campo label="Val COFINS" width={110}>
                <input type="text" inputMode="decimal" value={valorCofins} readOnly={travadoPorXml} style={travadoPorXml ? estiloXml : undefined} onChange={e => setValorCofins(e.target.value)} />
              </Campo>
              <Campo label="Val CSLL" width={110}>
                <input type="text" inputMode="decimal" value={valorCsll} readOnly={travadoPorXml} style={travadoPorXml ? estiloXml : undefined} onChange={e => setValorCsll(e.target.value)} />
              </Campo>
            </>
          )}
          <Campo label="Base do ICMS ST" width={110}>
            <input type="text" inputMode="decimal" value={baseIcmsSt} readOnly={travadoPorXml} style={travadoPorXml ? estiloXml : undefined} onChange={e => setBaseIcmsSt(e.target.value)} />
          </Campo>
          <Campo label="Val ICMS ST" width={110}>
            <input type="text" inputMode="decimal" value={valorIcmsSt} readOnly={travadoPorXml} style={travadoPorXml ? estiloXml : undefined} onChange={e => setValorIcmsSt(e.target.value)} />
          </Campo>
        </Secao>

        <Secao titulo="Observação" colapsavel>
          <Campo label="Observação" width={760}>
            <textarea
              value={observacao} readOnly={travadoPorXml} rows={1} maxLength={600}
              // Ref por callback (não useRef) porque essa seção começa fechada — o
              // textarea só existe no DOM quando o usuário expande, então precisa
              // recalcular a altura assim que ele é criado, não só quando o texto muda.
              ref={el => {
                if (!el) return
                el.style.height = 'auto'
                el.style.height = `${el.scrollHeight}px`
              }}
              style={{
                width: '100%', resize: 'none', overflow: 'hidden', fontFamily: 'inherit', fontSize: '0.78rem',
                ...(travadoPorXml ? estiloXml : {}),
              }}
              onChange={e => setObservacao(e.target.value)}
            />
          </Campo>
        </Secao>

        <Secao titulo="Totais" colapsavel>
          <div style={{
            width: '100%', background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e',
            borderRadius: 6, padding: '0.4rem 0.7rem', fontSize: '0.74rem', fontWeight: 600, marginBottom: '0.2rem',
          }}>
            ℹ Valores calculados automaticamente após o lançamento da nota — não editáveis.
          </div>
          <Campo label="Quantidade" width={110}>
            <input type="text" inputMode="decimal" value={quantidade} readOnly disabled style={ESTILO_TOTAL} />
          </Campo>
          <div style={{ width: '100%' }} />
          <Campo label="Base Trib.ICMS" width={110}>
            <input type="text" inputMode="decimal" value={baseTribIcms} readOnly disabled style={ESTILO_TOTAL} />
          </Campo>
          <Campo label="Val do ICMS" width={110}>
            <input type="text" inputMode="decimal" value={valorIcms} readOnly disabled style={ESTILO_TOTAL} />
          </Campo>
          <Campo label="Base Trib.IPI" width={110}>
            <input type="text" inputMode="decimal" value={baseTribIpi} readOnly disabled style={ESTILO_TOTAL} />
          </Campo>
          <Campo label="Val do IPI" width={110}>
            <input type="text" inputMode="decimal" value={valorIpi} readOnly disabled style={ESTILO_TOTAL} />
          </Campo>
          <Campo label="Val Mercadoria" width={110}>
            <input type="text" inputMode="decimal" value={valorMercadoria} readOnly disabled style={ESTILO_TOTAL} />
          </Campo>
          <Campo label="Val da Nota" width={110}>
            <input type="text" inputMode="decimal" value={valorNota} readOnly disabled style={ESTILO_TOTAL} />
          </Campo>
          <div style={{ width: '100%' }} />
          <Campo label="Val do IBS" width={110}>
            <input type="text" inputMode="decimal" value={valorIbs} readOnly disabled style={ESTILO_TOTAL} />
          </Campo>
          <Campo label="Val do CBS" width={110}>
            <input type="text" inputMode="decimal" value={valorCbs} readOnly disabled style={ESTILO_TOTAL} />
          </Campo>
        </Secao>

        {itens.length > 0 && (
          <Secao titulo={`Itens Lançados (${itens.length})`}>
            <div style={{ width: '100%', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ textAlign: 'left', padding: '4px 6px' }}>Item</th>
                    <th style={{ textAlign: 'left', padding: '4px 6px' }}>Descrição</th>
                    <th style={{ textAlign: 'right', padding: '4px 6px' }}>Qtd.</th>
                    <th style={{ textAlign: 'center', padding: '4px 6px' }}>Un.</th>
                    <th style={{ textAlign: 'right', padding: '4px 6px' }}>Valor</th>
                    <th style={{ padding: '4px 6px' }} />
                  </tr>
                </thead>
                <tbody>
                  {itens.map((it, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '4px 6px' }}>{it.itemCod}</td>
                      <td style={{ padding: '4px 6px' }}>{it.itemDes}</td>
                      <td style={{ padding: '4px 6px', textAlign: 'right' }}>{fmtQtd(it.quantidade)}</td>
                      <td style={{ padding: '4px 6px', textAlign: 'center' }}>{it.itemUn}</td>
                      <td style={{ padding: '4px 6px', textAlign: 'right' }}>R$ {fmtMoeda(it.valorTotal)}</td>
                      <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>
                        <button className="btn-editar" style={{ marginRight: 4 }} onClick={() => abrirEditarItem(idx)}>Editar</button>
                        <button className="btn-editar" onClick={() => removerItem(idx)}>Remover</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Secao>
        )}

        {erroSalvar && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626',
            borderRadius: 6, padding: '0.5rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, marginTop: '0.5rem',
          }}>
            {erroSalvar}
          </div>
        )}
        {/* Capa gravada não é nota lançada: só vale depois do Concluir */}
        {notaSalva && (
          <div style={{
            background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e',
            borderRadius: 6, padding: '0.5rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, marginTop: '0.5rem',
          }}>
            ✎ Nota Fiscal nº {notaSalva.numero} em lançamento — lance os itens e clique em "Concluir Nota" para finalizar.
          </div>
        )}
        {notaSalva && qtdItensXml !== null && itens.length < qtdItensXml && (
          <div style={{
            background: '#fef2f2', border: '2px solid #dc2626', color: '#991b1b',
            borderRadius: 6, padding: '0.7rem 1rem', fontSize: '0.9rem', fontWeight: 700, marginTop: '0.5rem',
          }}>
            🚨 ATENÇÃO: essa nota veio do XML com {qtdItensXml} item(ns), mas só {itens.length} {itens.length === 1 ? 'foi lançado' : 'foram lançados'} até agora.
            A nota <u>não fica registrada como digitada no Manifestador</u> enquanto faltar item — lance os {qtdItensXml - itens.length} que restam antes de sair dessa tela.
          </div>
        )}
        {notaSalva && nfseXml && itens.length > 0 && nfseXml.total - somaValorItens(itens) >= 0.01 && (
          <div style={{
            background: '#fef2f2', border: '2px solid #dc2626', color: '#991b1b',
            borderRadius: 6, padding: '0.7rem 1rem', fontSize: '0.9rem', fontWeight: 700, marginTop: '0.5rem',
          }}>
            🚨 ATENÇÃO: os itens somam R$ {fmtMoeda(String(somaValorItens(itens)))}, mas a NFS-e é de R$ {fmtMoeda(String(nfseXml.total))}.
            Faltam R$ {fmtMoeda(String(nfseXml.total - somaValorItens(itens)))} — clique em "Adicionar Item" pra lançar o restante antes de concluir.
          </div>
        )}
        {avisoConcluir && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626',
            borderRadius: 6, padding: '0.5rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, marginTop: '0.5rem',
          }}>
            ⚠ {avisoConcluir}
          </div>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
          {!notaSalva ? (
            <>
              <button className="btn btn-primary btn-sm" disabled={salvando} onClick={handleConfirmar}>
                {salvando ? 'Salvando...' : 'Confirmar'}
              </button>
              <button className="btn btn-secondary btn-sm" disabled={salvando} onClick={() => window.location.reload()}>
                Cancelar
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-primary btn-sm" style={BTN_DESTAQUE} onClick={abrirNovoItem}>
                + Novo Item
              </button>
              <button
                className="btn btn-sm"
                style={{ ...BTN_DESTAQUE, background: '#15803d', borderColor: '#15803d', color: '#fff' }}
                onClick={abrirFinanceiro}
              >
                {financeiroSalvo ? '✓ Financeiro' : financeiroDispensado ? 'Sem Financeiro' : 'Financeiro'}
              </button>
              <button
                className="btn btn-sm"
                style={{ ...BTN_DESTAQUE, background: '#0f766e', borderColor: '#0f766e', color: '#fff' }}
                onClick={concluirNota}
              >
                ✓ Concluir Nota
              </button>
              <button className="btn btn-secondary btn-sm" onClick={lancarNovaNota}>
                Lançar Nova Nota
              </button>
            </>
          )}
        </div>
      </div>

      {saidaNaoConcluida && (() => {
        const falta = nfseXml ? Math.round((nfseXml.total - somaValorItens(itens)) * 100) / 100 : 0
        return (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '1.5rem',
            width: 500, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 0.6rem', fontSize: '1rem', fontWeight: 700, color: '#b91c1c' }}>
              Nota não concluída
            </h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#374151', lineHeight: 1.45 }}>
              A nota {notaSalva?.numero} tem {itens.length} {itens.length === 1 ? 'item lançado' : 'itens lançados'}, mas ainda não foi concluída.
              {nfseXml && falta >= 0.01 && <> Os itens somam R$ {fmtMoeda(String(somaValorItens(itens)))} e a NFS-e é de R$ {fmtMoeda(String(nfseXml.total))} — faltam <b>R$ {fmtMoeda(String(falta))}</b>.</>}
              {' '}Para sair, conclua a nota ou exclua o lançamento (a nota volta a ficar pendente no Manifestador).
            </p>
            {erroExcluirNota && (
              <div style={{ marginBottom: '0.8rem', fontSize: '0.8rem', color: '#b91c1c' }}>{erroExcluirNota}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button className="btn-editar" disabled={excluindoNota}
                onClick={() => { setSaidaNaoConcluida(null); setErroExcluirNota('') }}>
                Continuar lançando
              </button>
              {(!nfseXml || falta < 0.01) && (
                <button className="btn btn-sm" disabled={excluindoNota} style={{ background: '#0f766e', borderColor: '#0f766e', color: '#fff' }}
                  onClick={() => { setSaidaNaoConcluida(null); concluirNota() }}>
                  ✓ Concluir agora
                </button>
              )}
              <button disabled={excluindoNota} onClick={excluirNotaESair}
                style={{ padding: '0.45rem 0.9rem', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
                {excluindoNota ? 'Excluindo...' : 'Excluir a nota e sair'}
              </button>
            </div>
          </div>
        </div>
        )
      })()}

      {saidaPendente && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '1.5rem',
            width: 460, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 0.6rem', fontSize: '1rem', fontWeight: 700, color: '#b91c1c' }}>
              Nota sem itens
            </h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#374151', lineHeight: 1.45 }}>
              A nota {notaSalva?.numero} já foi salva, mas nenhum item foi lançado. Uma nota não pode ficar sem itens:
              continue lançando ou exclua a nota para sair.
            </p>
            {erroExcluirCapa && (
              <div style={{ marginBottom: '0.8rem', fontSize: '0.8rem', color: '#b91c1c' }}>{erroExcluirCapa}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button className="btn-editar" disabled={excluindoCapa}
                onClick={() => { setSaidaPendente(null); setErroExcluirCapa('') }}>
                Continuar lançando
              </button>
              <button disabled={excluindoCapa} onClick={excluirCapaESair}
                style={{ padding: '0.45rem 0.9rem', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
                {excluindoCapa ? 'Excluindo...' : 'Excluir a nota e sair'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalVinculadasAberto && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => setModalVinculadasAberto(false)}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '1.5rem',
            width: 680, maxWidth: '95vw', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.3rem', fontSize: '1rem', fontWeight: 700, color: '#1a6ea8' }}>
              Ordens em Aberto — {fornecedorDes || 'Fornecedor'}
            </h3>
            <p style={{ margin: '0 0 0.8rem', fontSize: '0.76rem', color: '#6b7280' }}>
              Ordens de compra ainda não atendidas nos últimos 30 dias para este fornecedor.
            </p>
            {carregandoVinculadas && <div style={{ padding: '1.5rem 0', textAlign: 'center', color: '#6b7280' }}>Carregando...</div>}
            {!carregandoVinculadas && ordensVinculadas && ordensVinculadas.length === 0 && (
              <div style={{ padding: '1.5rem 0', textAlign: 'center', color: '#6b7280' }}>
                Nenhuma ordem em aberto nos últimos 30 dias para este fornecedor.
              </div>
            )}
            {!carregandoVinculadas && ordensVinculadas && ordensVinculadas.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Número</th>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Dt.Emis</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}>Qte Total</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}>Vl Total</th>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Comprador</th>
                    <th style={{ padding: '4px 6px', width: 90 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {ordensVinculadas.map(o => (
                    <tr key={o.numero} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '4px 6px', fontWeight: 700 }}>
                        <span onClick={() => abrirResumoOrdem(o.numero)}
                          style={{ cursor: 'pointer', color: '#1d4ed8', textDecoration: 'underline' }}
                          title="Clique para ver o resumo da ordem">
                          {o.numero}
                        </span>
                      </td>
                      <td style={{ padding: '4px 6px' }}>{o.dtemis ? new Date(o.dtemis).toLocaleDateString('pt-BR') : '-'}</td>
                      <td style={{ padding: '4px 6px', textAlign: 'right' }}>{Number(o.qtetotal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3 })}</td>
                      <td style={{ padding: '4px 6px', textAlign: 'right' }}>{Number(o.vltotal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                      <td style={{ padding: '4px 6px' }}>{o.comprador || '-'}</td>
                      <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                        <button onClick={() => { usarOrdemCompra(o.numero, o.compradorcod != null ? String(o.compradorcod) : undefined, o.comprador); setModalVinculadasAberto(false) }}
                          style={{ fontSize: '0.72rem', padding: '3px 8px', borderRadius: 4, border: '1px solid #1a6ea8', background: '#eff6ff', color: '#1a6ea8', cursor: 'pointer', fontWeight: 600 }}>
                          Usar esta
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn-editar" onClick={() => setModalVinculadasAberto(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {resumoOrdem && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          paddingTop: '4rem', zIndex: 1100,
        }} onClick={fecharResumoOrdem}>
          <div style={{ background: '#fff', borderRadius: 8, width: 680, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ background: '#1a6ea8', color: '#fff', padding: '0.9rem 1.25rem', borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 700 }}>Ordem de Compra nº {resumoOrdem.numero}</div>
                <div style={{ fontSize: '0.78rem', opacity: 0.85 }}>Estabelecimento {resumoOrdem.est}</div>
              </div>
              <button onClick={fecharResumoOrdem} style={{ background: 'rgba(255,255,255,0.18)', border: 'none', cursor: 'pointer', color: '#fff', borderRadius: 4, padding: '4px 10px', fontSize: '0.9rem' }}>✕</button>
            </div>

            {resumoOrdemCarregando && <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Carregando...</div>}

            {!resumoOrdemCarregando && !resumoOrdemDados && (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#b91c1c' }}>Não foi possível carregar os dados.</div>
            )}

            {!resumoOrdemCarregando && resumoOrdemDados && (
              <div style={{ padding: '1.1rem 1.25rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1.5rem', fontSize: '0.82rem', marginBottom: '0.9rem' }}>
                  <div><b>Data Emissão:</b> {fmtDataBr(resumoOrdemDados.dataEmissao)}</div>
                  <div><b>Fornecedor:</b> {resumoOrdemDados.fornecedorDes || '-'}</div>
                  <div><b>Comprador:</b> {resumoOrdemDados.compradorDes || '-'}</div>
                  <div><b>Usuário Inclusão:</b> {resumoOrdemDados.usuarioInclusao || '-'}</div>
                  <div><b>Data Inclusão:</b> {fmtDataBr(resumoOrdemDados.dataInclusao)}</div>
                </div>
                {resumoOrdemDados.observacao && (
                  <div style={{ fontSize: '0.82rem', marginBottom: '0.9rem' }}>
                    <b>Observação:</b> {resumoOrdemDados.observacao}
                  </div>
                )}

                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '0.7rem 0.9rem', marginBottom: '0.9rem' }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '0.4rem' }}>
                    Financeiro
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1.5rem', fontSize: '0.82rem' }}>
                    <div><b>Condição Pgto:</b> {resumoOrdemDados.condicaoPgtoDes || '-'}</div>
                    <div><b>Tipo Cobrança:</b> {resumoOrdemDados.tipoCobranca || '-'}</div>
                    <div><b>Valor Frete:</b> R$ {fmtMoeda(String(resumoOrdemDados.valorFrete))}</div>
                    <div><b>Valor Desconto:</b> R$ {fmtMoeda(String(resumoOrdemDados.valorDesconto))}</div>
                  </div>
                </div>

                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1a6ea8', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '0.4rem' }}>
                  Itens ({resumoOrdemDados.itens?.length ?? 0})
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Item</th>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Descrição</th>
                      <th style={{ textAlign: 'right', padding: '4px 6px' }}>Qtd.</th>
                      <th style={{ textAlign: 'center', padding: '4px 6px' }}>Un.</th>
                      <th style={{ textAlign: 'right', padding: '4px 6px' }}>Preço</th>
                      <th style={{ textAlign: 'right', padding: '4px 6px' }}>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resumoOrdemDados.itens?.map((it: any) => (
                      <tr key={it.linha} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '4px 6px' }}>{it.itemCod}</td>
                        <td style={{ padding: '4px 6px' }}>{it.itemDes}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'right' }}>{fmtQtd(it.quantidade)}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'center' }}>{it.unidade}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'right' }}>{fmtMoeda(it.precoUnitario)}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'right' }}>{fmtMoeda(it.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div style={{ display: 'flex', gap: 8, marginTop: '1.1rem' }}>
                  <button className="btn btn-primary btn-sm"
                    onClick={() => { usarOrdemCompra(resumoOrdem.numero, resumoOrdemDados?.compradorCod, resumoOrdemDados?.compradorDes); fecharResumoOrdem(); setModalVinculadasAberto(false) }}>
                    Usar esta Ordem
                  </button>
                  <button className="btn btn-secondary btn-sm"
                    onClick={() => window.open(`/api/erp/ordem-compra/${resumoOrdem.est}/${resumoOrdem.numero}/comprovante`, '_blank')}>
                    Imprimir Comprovante
                  </button>
                  <button className="btn-editar" onClick={fecharResumoOrdem}>
                    Fechar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {modalItemAberto && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          paddingTop: '2rem', zIndex: 1200,
        }} onClick={() => setModalItemAberto(false)}>
          <div style={{ background: '#fff', borderRadius: 8, width: 880, maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ background: '#1a6ea8', color: '#fff', padding: '0.9rem 1.25rem', borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 700 }}>
                  {itemEditandoIdx !== null ? `Editar Item ${itemEditandoIdx + 1}` : `Novo Item (${itens.length + 1} de ${itens.length + 1})`}
                </div>
                <div style={{ fontSize: '0.78rem', opacity: 0.85 }}>
                  {(empresaAtual?.empfan || empresaAtual?.empnom || '-')} · {fornecedorDes || 'Fornecedor'} · NF {numeroNota || '-'} · Emissão {fmtDataBr(dataEmissao)} · Receb. {fmtDataBr(dataRecebimento)}
                </div>
              </div>
              <button onClick={() => setModalItemAberto(false)} style={{ background: 'rgba(255,255,255,0.18)', border: 'none', cursor: 'pointer', color: '#fff', borderRadius: 4, padding: '4px 10px', fontSize: '0.9rem' }}>✕</button>
            </div>

            <div style={{ padding: '1rem 1.25rem' }}>
              <Secao titulo="Dados do Item">
                <Campo label="Ordem de Compra nº" width={100}>
                  <input
                    type="text" inputMode="numeric" value={itemForm.ordemCompraNum}
                    onChange={e => {
                      const v = e.target.value.replace(/\D/g, '')
                      setItemForm(f => ({ ...f, ordemCompraNum: v, ordemCompraLinha: v ? f.ordemCompraLinha : '' }))
                    }}
                  />
                </Campo>
                <Campo label="Linha" width={70}>
                  <input
                    type="text" inputMode="numeric" value={itemForm.ordemCompraLinha}
                    disabled={!itemForm.ordemCompraNum}
                    onChange={e => campoItem('ordemCompraLinha', e.target.value.replace(/\D/g, ''))}
                    onBlur={carregarItemDaOrdemCompra}
                    onKeyDown={e => { if (e.key === 'Enter') carregarItemDaOrdemCompra() }}
                  />
                </Campo>
                {carregandoItemOrdem && (
                  <div style={{ width: '100%', fontSize: '0.75rem', color: '#6b7280' }}>Carregando item da ordem de compra...</div>
                )}
                {avisoOrdemItem && (
                  <div style={{ width: '100%', fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>⚠ {avisoOrdemItem}</div>
                )}
                <div style={{ width: '100%' }} />
                <Campo label="Item" width={380}>
                  <ItemSelect
                    somenteServico={modDoctoFiscal === '99'} mostrarTipo
                    codValue={itemForm.itemCod} descValue={itemForm.itemDes} unValue={itemForm.itemUn}
                    onChange={(cod, des, un, _precod, _ncm, _cest, _forcod, esp) => setItemForm(f => ({
                      ...f, itemCod: cod, itemDes: des, itemUn: un, itemTipo: esp === 2 ? 'S' : 'P',
                    }))}
                  />
                </Campo>
                <Campo label="Descrição Complementar" width={340}>
                  <textarea
                    ref={descComplementarRef}
                    value={itemForm.descComplementar} maxLength={modDoctoFiscal === '99' ? 500 : 120}
                    rows={1}
                    style={{ width: '100%', resize: 'none', overflow: 'hidden', fontFamily: 'inherit', fontSize: '0.85rem', lineHeight: 1.3 }}
                    onChange={e => campoItem('descComplementar', e.target.value)}
                  />
                </Campo>
                <Campo label="Quantidade" width={110}>
                  <input type="text" inputMode="decimal" value={itemForm.quantidade} readOnly={itemFormTravadoXml}
                    style={itemFormTravadoXml ? estiloXml : undefined}
                    onChange={e => campoItem('quantidade', e.target.value)} />
                </Campo>
                <Campo label="Unidade Medida" width={90}>
                  <input type="text" value={itemForm.itemUn} readOnly disabled />
                </Campo>
                <Campo label="Val. Unitário" width={110}>
                  <input type="text" value={fmtUnit(valorUnitarioItem(itemForm))} readOnly disabled style={{ textAlign: 'right' }} />
                </Campo>
                <Campo label="Val. Total" width={110}>
                  {/* NFS-e importada: valor editável pra dividir o serviço em vários itens (IBS/CBS rateado) */}
                  <input type="text" inputMode="decimal" value={itemForm.valorTotal}
                    readOnly={itemFormTravadoXml && !nfseXml}
                    disabled={!itemFormTravadoXml && parseNumBr(itemForm.quantidade) <= 0}
                    title={parseNumBr(itemForm.quantidade) <= 0 ? 'Informe a Quantidade primeiro' : undefined}
                    style={itemFormTravadoXml && !nfseXml ? estiloXml : undefined}
                    onChange={e => nfseXml
                      ? setItemForm(f => rateioNfse({ ...f, valorTotal: e.target.value }, parseNumBr(e.target.value), itens.filter((_, i) => i !== itemEditandoIdx)))
                      : campoItem('valorTotal', e.target.value)} />
                </Campo>
                <Campo label="Natureza Operação / CFOP *" width={600}>
                  {/* Busca pelo código da natureza (igual à capa) e mostra a CFOP dela. */}
                  <NaturezaSelect naturezas={naturezasParaSelecionar} value={itemForm.naturezaCod}
                    onChange={cod => {
                      const nat = naturezas.find(n => String(n.tno_cod) === cod)
                      setItemForm(f => ({ ...f, naturezaCod: cod, cfopCod: nat ? String(nat.tno_cdf) : '', cfopDes: nat?.tno_dsc || '' }))
                    }} />
                </Campo>
                <Campo label="Descrição da CFOP" width={420}>
                  <input type="text" value={itemForm.cfopDes} readOnly
                    style={{ background: '#eff6ff', borderColor: '#93c5fd', color: '#1a6ea8', fontWeight: 700, fontSize: '0.74rem' }} />
                </Campo>
                {modDoctoFiscal === '99' ? (
                <Campo label="Código NBS" width={760}>
                  {/* NFS-e: NBS no lugar do NCM (cNBS + xNBS do XML). */}
                  {itemFormTravadoXml ? (
                  <input type="text" readOnly style={estiloXml}
                    value={itemForm.nbsCod ? `${itemForm.nbsCod}${itemForm.nbsDes ? ` - ${itemForm.nbsDes}` : ''}` : ''}
                    title={itemForm.nbsDes || undefined} />
                  ) : (
                  <input type="text" inputMode="numeric" maxLength={9} placeholder="9 dígitos" style={{ width: 130 }}
                    value={itemForm.nbsCod}
                    onChange={e => setItemForm(f => ({ ...f, nbsCod: e.target.value.replace(/\D/g, '').slice(0, 9), nbsDes: '' }))} />
                  )}
                </Campo>
                ) : (
                <Campo label="Código NCM" width={130}>
                  {itemFormTravadoXml ? (
                    <input type="text" value={`${itemForm.ncmCod}${itemForm.ncmDes ? ` - ${itemForm.ncmDes}` : ''}`} readOnly style={estiloXml} />
                  ) : (
                  <LookupSelect
                    endpoint="/erp/ncm/lista" codKey="ncm_cod" descKey="ncm_des" tagKey="ncm_cod"
                    // ncmDes não vem do XML (só o código) — sem o fallback pro código, o
                    // LookupSelect escondia o valor (só exibe algo quando descValue existe),
                    // mesmo com o NCM já salvo. Item digitado na mão continua tendo os dois.
                    codValue={itemForm.ncmCod} descValue={itemForm.ncmDes || itemForm.ncmCod}
                    valorExibido={itemForm.ncmCod}
                    onSelect={(cod, des) => setItemForm(f => ({ ...f, ncmCod: cod, ncmDes: des }))}
                    placeholder="Buscar NCM por código ou descrição..." listWidth={420}
                    somenteComTexto
                  />
                  )}
                </Campo>
                )}
              </Secao>

              <div style={{ display: 'flex', gap: '0.3rem', borderBottom: '2px solid #eff6ff', marginBottom: '0.8rem' }}>
                {(['geral', 'tributacao'] as const).map(t => (
                  <button key={t} type="button" onClick={() => setTabItem(t)}
                    style={{
                      padding: '0.4rem 0.9rem', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
                      border: 'none', borderBottom: tabItem === t ? '2px solid #1a6ea8' : '2px solid transparent',
                      marginBottom: '-2px', background: 'transparent', color: tabItem === t ? '#1a6ea8' : '#6b7280',
                    }}>
                    {t === 'geral' ? 'Geral' : 'Complemento/Impostos'}
                  </button>
                ))}
              </div>

              {tabItem === 'geral' && (
                <Secao titulo="Frota / Consumo">
                  <Campo label="Origem" width={260}>
                    <LookupSelect
                      endpoint="/erp/oedcad/lista" codKey="oed_cod" descKey="oed_des" tagKey="oed_cod"
                      codValue={itemForm.origemCod} descValue={itemForm.origemDes}
                      onSelect={(cod, des) => setItemForm(f => ({ ...f, origemCod: cod, origemDes: des }))}
                      placeholder="Buscar origem..." listWidth={300}
                    />
                  </Campo>
                  <Campo label="Destino" width={260}>
                    <LookupSelect
                      endpoint="/erp/oedcad/lista" codKey="oed_cod" descKey="oed_des" tagKey="oed_cod"
                      codValue={itemForm.destinoCod} descValue={itemForm.destinoDes}
                      onSelect={(cod, des, item) => setItemForm(f => {
                        const ehEstoque = item?.oed_tip === 5
                        const informaVeiculo = item?.oed_vei === 'S'
                        return {
                          ...f, destinoCod: cod, destinoDes: des, destinoTipo: item?.oed_tip ?? '',
                          destinoVeiSempre: informaVeiculo,
                          // Sem veículo não há OS pra gerar/vincular/incluir.
                          veiculoCod: informaVeiculo ? f.veiculoCod : '', veiculoDes: informaVeiculo ? f.veiculoDes : '',
                          osAutomatica: informaVeiculo ? f.osAutomatica : 'N',
                          // Destino Estoque (tipo 5) não aceita rateio de custo (regra da PCCO019).
                          rateioCusto: ehEstoque ? '' : f.rateioCusto,
                          // Conta Estoque x Aplicação são mutuamente exclusivos: Estoque exige
                          // Conta Estoque e bloqueia Aplicação; fora disso é o contrário.
                          contaEstoqueCod: ehEstoque ? f.contaEstoqueCod : '',
                          contaEstoqueDes: ehEstoque ? f.contaEstoqueDes : '',
                          aplicacaoCod: ehEstoque ? '' : f.aplicacaoCod,
                          aplicacaoDes: ehEstoque ? '' : f.aplicacaoDes,
                        }
                      })}
                      placeholder="Buscar destino..." listWidth={300}
                    />
                  </Campo>

                  {itemForm.destinoVeiSempre && (
                    <>
                      <Divisor titulo="Veículo" />
                      <Campo label="Veículo" width={150}>
                        <LookupSelect
                          endpoint="/erp/veiculos" codKey="veicod" descKey="veipla" tagKey="veicod"
                          codValue={itemForm.veiculoCod} descValue={itemForm.veiculoDes}
                          onSelect={async (cod, des) => {
                            setItemForm(f => ({ ...f, veiculoCod: cod, veiculoDes: des }))
                            try {
                              const r = await api.get(`/erp/veiculos/${cod}/hodometro`, {
                                params: { empresa: empresaCod ?? 1, data: dataEmissao || hojeISO() },
                              })
                              if (r.data?.hodometro != null) {
                                setItemForm(f => ({ ...f, hodometro: f.hodometro ? f.hodometro : String(r.data.hodometro) }))
                              }
                            } catch { /* hodômetro default é best-effort, usuário digita manual se falhar */ }
                          }}
                          placeholder="Buscar por placa ou código..." listWidth={300}
                        />
                      </Campo>
                      <Campo label="Hodômetro" width={100}>
                        <input type="text" inputMode="numeric" value={itemForm.hodometro} onChange={e => campoItem('hodometro', e.target.value.replace(/\D/g, ''))} />
                      </Campo>
                      <Campo label="Hora" width={90}>
                        <input type="text" inputMode="decimal" value={itemForm.hora}
                          onChange={e => campoItem('hora', e.target.value.replace(/[^0-9.]/g, ''))}
                          placeholder="HH.MM" />
                      </Campo>
                    </>
                  )}

                  {/* Pneu só em NFS-e: recapagem/conserto é serviço, nunca vem em NF-e modelo 55 */}
                  {modDoctoFiscal === '99' && (
                    <>
                      <Divisor titulo="Pneu" />
                      <Campo label="Cód. Pneu" width={120}>
                        <LookupSelect
                          endpoint="/erp/pnucad/lista" codKey="pnucod" descKey="pnu_des" tagKey="pnucod"
                          extraParams={{ somenteEnvioAberto: 1 }}
                          codValue={itemForm.pneuCod} descValue={itemForm.pneuDes} valorExibido={itemForm.pneuCod}
                          onSelect={(cod, des) => setItemForm(f => ({ ...f, pneuCod: cod, pneuDes: des }))}
                          placeholder="Pneu com envio pendente..." listWidth={320}
                        />
                      </Campo>
                    </>
                  )}
                  {modDoctoFiscal === '99' && !!itemForm.pneuCod && (
                    <Campo label="Procedimento" width={130}>
                      <select value={itemForm.recapagem} onChange={e => campoItem('recapagem', e.target.value)}>
                        <option value="S">Recapagem</option>
                        <option value="N">Conserto</option>
                      </select>
                    </Campo>
                  )}

                  {itemForm.destinoVeiSempre && (
                    <>
                      <Divisor titulo="Abastecimento" />
                      <Campo label="Posto/Praça" width={260}>
                        <LookupSelect
                          endpoint="/erp/pracas/lista" codKey="pracod" descKey="pranom" tagKey="pracod"
                          codValue={itemForm.postoCod} descValue={itemForm.postoDes}
                          onSelect={async (cod, des) => {
                            setItemForm(f => ({ ...f, postoCod: cod, postoDes: des }))
                            if (!itemForm.veiculoCod) return
                            try {
                              const r = await api.get(`/erp/veiculos/${itemForm.veiculoCod}/combustivel`)
                              if (r.data?.letra) setItemForm(f => ({ ...f, tipoCombustivel: r.data.letra }))
                            } catch { /* tipo de combustível é best-effort, segue vazio se falhar */ }
                          }}
                          placeholder="Buscar posto/praça..." listWidth={300}
                        />
                      </Campo>
                      <Campo label="Tipo de Combustível" width={100}>
                        <input type="text" value={itemForm.tipoCombustivel} readOnly disabled />
                      </Campo>
                    </>
                  )}

                  {itemForm.destinoVeiSempre && (
                    <>
                      <Divisor titulo="Ordem de Serviço" />
                      <Campo label="O.Serviço Automática?" width={170}>
                        <select value={itemForm.osAutomatica} onChange={e => setItemForm(f => ({
                          ...f, osAutomatica: e.target.value,
                          ordemServicoCod: '', ordemServicoDes: '', ordemServicoLinha: '', osExecutada: false,
                          tipoServicoCod: '', tipoServicoDes: '',
                        }))}>
                          <option value="N">Não</option>
                          <option value="G">Gerar OS/Serviço</option>
                          <option value="V">Vincular à OS</option>
                          <option value="I">Incluir Serviço</option>
                        </select>
                      </Campo>

                      {itemForm.osAutomatica === 'V' && (
                        <Campo label="Ordem de Serviço" width={150}>
                          <div className="nat-wrap">
                            <div className="nat-desc-box" onClick={abrirOrdensServico} tabIndex={0} role="button">
                              <span style={{ flexShrink: 0, fontSize: '0.85rem', color: '#1a6ea8', opacity: 0.6 }}>🔍</span>
                              {itemForm.ordemServicoCod ? (
                                <span className="nat-dsc">OS {itemForm.ordemServicoCod} (linha {itemForm.ordemServicoLinha})</span>
                              ) : (
                                <span className="nat-placeholder">Clique para escolher a OS...</span>
                              )}
                            </div>
                          </div>
                        </Campo>
                      )}

                      {itemForm.osAutomatica === 'I' && (
                        <Campo label="Nº da OS" width={110}>
                          <input type="text" inputMode="numeric" value={itemForm.ordemServicoCod}
                            onChange={e => campoItem('ordemServicoCod', e.target.value.replace(/\D/g, ''))} />
                        </Campo>
                      )}

                      {(itemForm.osAutomatica === 'G' || itemForm.osAutomatica === 'I') && (
                        <Campo label="Tipo de Serviço" width={300}>
                          <LookupSelect
                            endpoint="/erp/serman/lista" codKey="sercod" descKey="serdes" tagKey="sercod"
                            codValue={itemForm.tipoServicoCod} descValue={itemForm.tipoServicoDes}
                            valorExibido={itemForm.tipoServicoCod ? `${itemForm.tipoServicoCod} - ${itemForm.tipoServicoDes}` : ''}
                            onSelect={(cod, des) => setItemForm(f => ({ ...f, tipoServicoCod: cod, tipoServicoDes: des }))}
                            placeholder="Buscar tipo de serviço..." listWidth={340}
                          />
                        </Campo>
                      )}
                    </>
                  )}
                  <Divisor titulo="Aplicação e Custo" />
                  {/* NFS-e é serviço: não tem Conta Estoque. */}
                  {modDoctoFiscal !== '99' && (
                  <Campo label={`Conta Estoque${itemForm.destinoTipo === 5 ? ' *' : ''}`} width={300}>
                    <LookupSelect
                      endpoint="/erp/ctecad" codKey="cte_cod" descKey="cte_des" tagKey="cte_cod"
                      codValue={itemForm.contaEstoqueCod} descValue={itemForm.contaEstoqueDes}
                      valorExibido={itemForm.contaEstoqueCod ? `${itemForm.contaEstoqueCod} - ${itemForm.contaEstoqueDes}` : ''}
                      onSelect={(cod, des) => setItemForm(f => ({ ...f, contaEstoqueCod: cod, contaEstoqueDes: des }))}
                      placeholder="Buscar conta estoque..." listWidth={300}
                      disabled={itemForm.destinoTipo !== 5}
                    />
                  </Campo>
                  )}
                  <Campo label="Centro de Custo" width={260}>
                    <CentroCustoSelect
                      value={itemForm.centroCustoCod}
                      onChange={(cod, des) => setItemForm(f => ({
                        ...f, centroCustoCod: cod, centroCustoDes: des,
                        // Sem Centro de Custo não há o que ratear (regra da PCCO019).
                        rateioCusto: cod ? f.rateioCusto : '', numeroParcelas: cod ? f.numeroParcelas : '',
                      }))}
                    />
                  </Campo>
                  <Campo label="Rateio do Custo" width={150}>
                    <select
                      value={itemForm.rateioCusto}
                      disabled={!itemForm.centroCustoCod || itemForm.destinoTipo === 5}
                      title={
                        itemForm.destinoTipo === 5 ? 'Destino Estoque não aceita rateio'
                          : !itemForm.centroCustoCod ? 'Informe o Centro de Custo pra ratear'
                          : undefined
                      }
                      onChange={e => setItemForm(f => ({ ...f, rateioCusto: e.target.value, numeroParcelas: e.target.value === 'P' ? f.numeroParcelas : '' }))}
                    >
                      <option value="">Nenhum</option>
                      <option value="P">Parcelamento</option>
                      <option value="C">Por Centro de Custos</option>
                      <option value="V">Rateio por Veículos</option>
                    </select>
                  </Campo>
                  {itemForm.rateioCusto === 'P' && (
                    <Campo label="Número de Parcelas" width={130}>
                      <input
                        type="text" inputMode="numeric" value={itemForm.numeroParcelas}
                        onChange={e => campoItem('numeroParcelas', e.target.value.replace(/\D/g, ''))}
                      />
                    </Campo>
                  )}
                  {/* Com Rateio "Por Centro de Custos" a Aplicação é lançada linha a linha
                      na tabela abaixo (grava direto na ntvccu) — pedir aqui de novo só
                      duplicaria: cairia no item (pnv_aplcod) E teria que repetir na tabela. */}
                  {itemForm.rateioCusto !== 'C' && (
                    <Campo label={`Aplicação${itemForm.destinoTipo !== 5 ? ' *' : ''}`} width={300}>
                      <LookupSelect
                        endpoint="/erp/aplicacoes" codKey="apl_cod" descKey="apl_des" tagKey="apl_cod"
                        codValue={itemForm.aplicacaoCod} descValue={itemForm.aplicacaoDes}
                        valorExibido={itemForm.aplicacaoCod ? `${itemForm.aplicacaoCod} - ${itemForm.aplicacaoDes}` : ''}
                        onSelect={(cod, des) => setItemForm(f => ({ ...f, aplicacaoCod: cod, aplicacaoDes: des }))}
                        placeholder="Buscar aplicação..." listWidth={300}
                        disabled={itemForm.destinoTipo === 5}
                      />
                    </Campo>
                  )}
                  {itemForm.rateioCusto === 'C' && (() => {
                    const totalItem = parseNumBr(itemForm.valorTotal)
                    const totalRateado = itemForm.rateioLinhas.reduce((s, l) => s + parseNumBr(l.valor), 0)
                    const bate = Math.abs(totalRateado - totalItem) < 0.01
                    function alterarLinha(idx: number, campo: 'percentual' | 'valor', valor: string) {
                      setItemForm(f => ({
                        ...f,
                        rateioLinhas: f.rateioLinhas.map((l, i) => {
                          if (i !== idx) return l
                          if (campo === 'percentual') {
                            const perc = parseNumBr(valor)
                            return { ...l, percentual: valor, valor: fmtMoeda(String(round2(totalItem * perc / 100))) }
                          }
                          const val = parseNumBr(valor)
                          return { ...l, valor, percentual: totalItem ? String(round2(val / totalItem * 100)).replace('.', ',') : '0' }
                        }),
                      }))
                    }
                    function adicionarLinha() {
                      setItemForm(f => ({ ...f, rateioLinhas: [...f.rateioLinhas, { centroCustoCod: '', centroCustoDes: '', aplicacaoCod: '', aplicacaoDes: '', percentual: '0', valor: '0,00' }] }))
                    }
                    function removerLinha(idx: number) {
                      setItemForm(f => ({ ...f, rateioLinhas: f.rateioLinhas.filter((_, i) => i !== idx) }))
                    }
                    return (
                      <div style={{ width: '100%', marginTop: '0.4rem' }}>
                        <Divisor titulo="Rateio por Centro de Custo — Contas Aplicação" />
                        <div style={{ width: '100%', overflowX: 'auto' }}>
                          <table style={{ width: '100%', maxWidth: 780, borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                            <thead>
                              <tr style={{ background: '#1a6ea8' }}>
                                <th style={{ color: '#fff', padding: '4px 6px', textAlign: 'left' }}>Centro de Custo</th>
                                <th style={{ color: '#fff', padding: '4px 6px', textAlign: 'left' }}>Conta Aplicação</th>
                                <th style={{ color: '#fff', padding: '4px 6px', textAlign: 'right', width: 90 }}>%</th>
                                <th style={{ color: '#fff', padding: '4px 6px', textAlign: 'right', width: 120 }}>Valor R$</th>
                                <th style={{ padding: '4px 6px', width: 36 }} />
                              </tr>
                            </thead>
                            <tbody>
                              {itemForm.rateioLinhas.map((l, idx) => (
                                <tr key={idx}>
                                  <td style={{ padding: '3px 6px', border: '1px solid #eef0f4' }}>
                                    <CentroCustoSelect
                                      value={l.centroCustoCod}
                                      onChange={(cod, des) => setItemForm(f => ({ ...f, rateioLinhas: f.rateioLinhas.map((x, i) => i === idx ? { ...x, centroCustoCod: cod, centroCustoDes: des } : x) }))}
                                    />
                                  </td>
                                  <td style={{ padding: '3px 6px', border: '1px solid #eef0f4' }}>
                                    <LookupSelect
                                      endpoint="/erp/aplicacoes" codKey="apl_cod" descKey="apl_des" tagKey="apl_cod"
                                      codValue={l.aplicacaoCod} descValue={l.aplicacaoDes}
                                      valorExibido={l.aplicacaoCod ? `${l.aplicacaoCod} - ${l.aplicacaoDes}` : ''}
                                      onSelect={(cod, des) => setItemForm(f => ({ ...f, rateioLinhas: f.rateioLinhas.map((x, i) => i === idx ? { ...x, aplicacaoCod: cod, aplicacaoDes: des } : x) }))}
                                      placeholder="Buscar aplicação..." listWidth={300}
                                    />
                                  </td>
                                  <td style={{ padding: '3px 6px', border: '1px solid #eef0f4' }}>
                                    <input type="text" inputMode="decimal" value={l.percentual} style={{ textAlign: 'right' }}
                                      onChange={e => alterarLinha(idx, 'percentual', e.target.value)} />
                                  </td>
                                  <td style={{ padding: '3px 6px', border: '1px solid #eef0f4' }}>
                                    <input type="text" inputMode="decimal" value={l.valor} style={{ textAlign: 'right' }}
                                      onChange={e => alterarLinha(idx, 'valor', e.target.value)} />
                                  </td>
                                  <td style={{ padding: '3px 6px', border: '1px solid #eef0f4', textAlign: 'center' }}>
                                    <button type="button" className="row-action-btn row-action-btn--danger" onClick={() => removerLinha(idx)}>×</button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={adicionarLinha} style={{ marginTop: '0.4rem' }}>+ Conta Aplicação</button>
                        <div style={{ fontSize: '0.78rem', marginTop: '0.3rem', color: bate ? '#166534' : '#dc2626', fontWeight: 600 }}>
                          Total rateado: R$ {fmtMoeda(String(totalRateado))} {bate ? '✓' : `(precisa bater com R$ ${fmtMoeda(String(totalItem))} do item)`}
                        </div>
                      </div>
                    )
                  })()}
                </Secao>
              )}

              {tabItem === 'tributacao' && (
                <Secao titulo="Complemento/Impostos">
                  {modDoctoFiscal !== '55' && modDoctoFiscal !== '99' && (
                    <>
                      <div style={{ width: '100%' }} />
                      <Campo label="Código Serviço" width={340}>
                        <ServicoPrestadoSelect
                          codValue={itemForm.servicoCod} desValue={itemForm.servicoDes}
                          onChange={(cod, des) => setItemForm(f => ({ ...f, servicoCod: cod, servicoDes: des }))}
                        />
                      </Campo>
                    </>
                  )}

                  <Divisor titulo="Frete, Seguro e Desconto" />
                  <Campo label="Valor Frete" width={110}>
                    <input type="text" inputMode="decimal" value={itemForm.itemValorFrete} readOnly={itemFormTravadoXml}
                      style={itemFormTravadoXml ? estiloXml : undefined}
                      onChange={e => campoItem('itemValorFrete', e.target.value)} />
                  </Campo>
                  <Campo label="Valor do Acréscimo" width={110}>
                    <input type="text" inputMode="decimal" value={itemForm.itemValorAcrescimo} readOnly={itemFormTravadoXml}
                      style={itemFormTravadoXml ? estiloXml : undefined}
                      onChange={e => campoItem('itemValorAcrescimo', e.target.value)} />
                  </Campo>
                  <Campo label="Valor do Seguro" width={110}>
                    <input type="text" inputMode="decimal" value={itemForm.itemValorSeguro} readOnly={itemFormTravadoXml}
                      style={itemFormTravadoXml ? estiloXml : undefined}
                      onChange={e => campoItem('itemValorSeguro', e.target.value)} />
                  </Campo>
                  <Campo label="Valor Desconto" width={110}>
                    <input type="text" inputMode="decimal" value={itemForm.itemValorDesconto} readOnly={itemFormTravadoXml}
                      style={itemFormTravadoXml ? estiloXml : undefined}
                      onChange={e => campoItem('itemValorDesconto', e.target.value)} />
                  </Campo>
                  <Campo label="Valor Contábil" width={110}>
                    <input type="text" inputMode="decimal" value={itemForm.valorContabil} readOnly disabled style={ESTILO_TOTAL} />
                  </Campo>

                  <Divisor titulo="Impostos" destaque />
                  <div style={{ width: '100%', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                      <thead>
                        <tr style={{ background: '#1a6ea8' }}>
                          {['Tributo', 'Base de Cálculo', 'Alíquota', 'Valor', 'CST'].map(h => (
                            <th key={h} style={{
                              padding: '5px 5px', border: '1px solid #1a6ea8', color: '#fff', fontWeight: 700,
                              fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.02em', textAlign: h === 'Tributo' ? 'left' : 'center',
                            }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {modDoctoFiscal !== '55' && (
                          <LinhaImposto
                            nome="INSS"
                            base={{ value: itemForm.inssBase, onChange: v => campoItem('inssBase', v) }}
                            perc={{ value: itemForm.inssPerc, onChange: v => campoItem('inssPerc', v) }}
                            valor={{ value: itemForm.inssValor, onChange: v => campoItem('inssValor', v) }}
                          />
                        )}
                        <LinhaImposto
                          nome="DIFAL"
                          valor={{ value: itemForm.difal, onChange: v => campoItem('difal', v) }}
                        />
                        <LinhaImposto
                          nome="ICMS" travado={itemFormTravadoXml}
                          base={{ value: itemForm.icmsBase, onChange: v => campoItem('icmsBase', v) }}
                          perc={{ value: itemForm.icmsPerc, onChange: v => campoItem('icmsPerc', v) }}
                          valor={{ value: itemForm.icmsValor, onChange: v => campoItem('icmsValor', v) }}
                          cst={{ value: itemForm.icmsCst, onChange: v => campoItem('icmsCst', v) }}
                        />
                        <LinhaImposto
                          nome="ICMS ST" travado={itemFormTravadoXml}
                          base={{ value: itemForm.icmsStBase, onChange: v => campoItem('icmsStBase', v) }}
                          perc={{ value: itemForm.icmsStPerc, onChange: v => campoItem('icmsStPerc', v) }}
                          valor={{ value: itemForm.icmsStValor, onChange: v => campoItem('icmsStValor', v) }}
                        />
                        <LinhaImposto
                          nome="IPI" travado={itemFormTravadoXml}
                          base={{ value: itemForm.ipiBase, onChange: v => campoItem('ipiBase', v) }}
                          perc={{ value: itemForm.ipiPerc, onChange: v => campoItem('ipiPerc', v) }}
                          valor={{ value: itemForm.ipiValor, onChange: v => campoItem('ipiValor', v) }}
                          cst={{ value: itemForm.ipiCst, onChange: v => campoItem('ipiCst', v) }}
                        />
                        <LinhaImposto
                          nome="PIS" travado motivo="cfop"
                          base={{ value: itemForm.pisBase, onChange: v => campoItem('pisBase', v) }}
                          perc={{ value: itemForm.pisPerc, onChange: v => campoItem('pisPerc', v) }}
                          valor={{ value: itemForm.pisValor, onChange: v => campoItem('pisValor', v) }}
                          cst={{ value: itemForm.pisCst, onChange: v => campoItem('pisCst', v) }}
                        />
                        <LinhaImposto
                          nome="Cofins" travado motivo="cfop"
                          base={{ value: itemForm.cofinsBase, onChange: v => campoItem('cofinsBase', v) }}
                          perc={{ value: itemForm.cofinsPerc, onChange: v => campoItem('cofinsPerc', v) }}
                          valor={{ value: itemForm.cofinsValor, onChange: v => campoItem('cofinsValor', v) }}
                          cst={{ value: itemForm.cofinsCst, onChange: v => campoItem('cofinsCst', v) }}
                        />
                        <LinhaImposto
                          nome="PIS (XML)" travado={itemFormTravadoXml} cor={DESTAQUE_XML_REF} cstFixo={false}
                          base={{ value: itemForm.pisXmlBase, onChange: v => campoItem('pisXmlBase', v) }}
                          perc={{ value: itemForm.pisXmlAliq, onChange: v => campoItem('pisXmlAliq', v) }}
                          valor={{ value: itemForm.pisXmlValor, onChange: v => campoItem('pisXmlValor', v) }}
                          cst={{ value: itemForm.pisXmlCst, onChange: v => campoItem('pisXmlCst', v) }}
                        />
                        <LinhaImposto
                          nome="Cofins (XML)" travado={itemFormTravadoXml} cor={DESTAQUE_XML_REF} cstFixo={false}
                          base={{ value: itemForm.cofinsXmlBase, onChange: v => campoItem('cofinsXmlBase', v) }}
                          perc={{ value: itemForm.cofinsXmlAliq, onChange: v => campoItem('cofinsXmlAliq', v) }}
                          valor={{ value: itemForm.cofinsXmlValor, onChange: v => campoItem('cofinsXmlValor', v) }}
                          cst={{ value: itemForm.cofinsXmlCst, onChange: v => campoItem('cofinsXmlCst', v) }}
                        />
                      </tbody>
                    </table>
                  </div>
                </Secao>
              )}

              {tabItem === 'tributacao' && (
                <Secao titulo="IBS/CBS/IS (Reforma Tributária)">
                  <Campo label="CST" width={70}>
                    <input type="text" value={itemForm.ibsCbsCst} readOnly={itemFormTravadoXml}
                      onChange={e => campoItem('ibsCbsCst', e.target.value.slice(0, 3))}
                      style={{ fontFamily: 'monospace', textAlign: 'center', ...(itemFormTravadoXml ? estiloXml : {}) }} />
                  </Campo>
                  <Campo label="cClassTrib" width={100}>
                    <input type="text" value={itemForm.ibsCbsClassTrib} readOnly={itemFormTravadoXml}
                      onChange={e => campoItem('ibsCbsClassTrib', e.target.value.slice(0, 6))}
                      style={{ fontFamily: 'monospace', textAlign: 'center', ...(itemFormTravadoXml ? estiloXml : {}) }} />
                  </Campo>
                  <Campo label="UF (IBS Estadual)" width={70}>
                    <input type="text" value={itemForm.ibsUfEst} readOnly={itemFormTravadoXml}
                      onChange={e => campoItem('ibsUfEst', e.target.value.toUpperCase().slice(0, 2))}
                      style={{ fontFamily: 'monospace', textAlign: 'center', ...(itemFormTravadoXml ? estiloXml : {}) }} />
                  </Campo>
                  {(cstDes(itemForm.ibsCbsCst) || classTribDes(itemForm.ibsCbsCst, itemForm.ibsCbsClassTrib)) && (
                    <div style={{ width: '100%', fontSize: '0.76rem', color: '#374151', lineHeight: 1.4 }}>
                      {cstDes(itemForm.ibsCbsCst) && <div><strong>CST {itemForm.ibsCbsCst}:</strong> {cstDes(itemForm.ibsCbsCst)}</div>}
                      {classTribDes(itemForm.ibsCbsCst, itemForm.ibsCbsClassTrib) && (
                        <div><strong>cClassTrib {itemForm.ibsCbsClassTrib}:</strong> {classTribDes(itemForm.ibsCbsCst, itemForm.ibsCbsClassTrib)}</div>
                      )}
                    </div>
                  )}
                  {itemForm.ibsCbsMono.length > 0 && (
                    <AbasIbsCbs aba={abaIbsCbs ?? 'mono'} onChange={setAbaIbsCbs} />
                  )}
                  {itemForm.ibsCbsMono.length > 0 && (abaIbsCbs ?? 'mono') === 'mono' && (
                    <TabelaIbsCbsMono linhas={itemForm.ibsCbsMono} unidade={itemForm.itemUn} />
                  )}
                  {(itemForm.ibsCbsMono.length === 0 || abaIbsCbs === 'regular') && (
                  <div style={{ width: '100%', overflowX: 'auto', marginTop: '0.4rem' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                      <thead>
                        <tr style={{ background: '#1a6ea8' }}>
                          {['Tributo', 'Base Cálculo', 'BC Efetiva', 'Ali.Ref %', 'Red.Ali %', 'Ali.Efet %', 'Valor'].map(h => (
                            <th key={h} style={{
                              padding: '5px 5px', border: '1px solid #1a6ea8', color: '#fff', fontWeight: 700,
                              fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.02em', textAlign: h === 'Tributo' ? 'left' : 'center',
                            }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <LinhaIbsCbs
                          nome="CBS" travado={itemFormTravadoXml}
                          base={{ value: itemForm.cbsBase, onChange: v => campoItem('cbsBase', v) }}
                          bcEf={{ value: itemForm.cbsBcEf, onChange: v => campoItem('cbsBcEf', v) }}
                          aliRef={{ value: itemForm.cbsAliRef, onChange: v => campoItem('cbsAliRef', v) }}
                          redAli={{ value: itemForm.cbsRedAli, onChange: v => campoItem('cbsRedAli', v) }}
                          aliEf={{ value: itemForm.cbsAliEf, onChange: v => campoItem('cbsAliEf', v) }}
                          valor={{ value: itemForm.cbsValor, onChange: v => campoItem('cbsValor', v) }}
                        />
                        <LinhaIbsCbs
                          nome="IBS UF" travado={itemFormTravadoXml}
                          bcEf={{ value: itemForm.ibsUfBcEf, onChange: v => campoItem('ibsUfBcEf', v) }}
                          aliRef={{ value: itemForm.ibsUfAliRef, onChange: v => campoItem('ibsUfAliRef', v) }}
                          redAli={{ value: itemForm.ibsUfRedAli, onChange: v => campoItem('ibsUfRedAli', v) }}
                          aliEf={{ value: itemForm.ibsUfAliEf, onChange: v => campoItem('ibsUfAliEf', v) }}
                          valor={{ value: itemForm.ibsUfValor, onChange: v => campoItem('ibsUfValor', v) }}
                        />
                        <LinhaIbsCbs
                          nome="IBS Mun." travado={itemFormTravadoXml}
                          base={{ value: itemForm.ibsMunBase, onChange: v => campoItem('ibsMunBase', v) }}
                          bcEf={{ value: itemForm.ibsMunBcEf, onChange: v => campoItem('ibsMunBcEf', v) }}
                          aliRef={{ value: itemForm.ibsMunAliRef, onChange: v => campoItem('ibsMunAliRef', v) }}
                          redAli={{ value: itemForm.ibsMunRedAli, onChange: v => campoItem('ibsMunRedAli', v) }}
                          aliEf={{ value: itemForm.ibsMunAliEf, onChange: v => campoItem('ibsMunAliEf', v) }}
                          valor={{ value: itemForm.ibsMunValor, onChange: v => campoItem('ibsMunValor', v) }}
                        />
                        <LinhaIbsCbs
                          nome="IS" travado={itemFormTravadoXml}
                          base={{ value: itemForm.isBase, onChange: v => campoItem('isBase', v) }}
                          aliEf={{ value: itemForm.isAliq, onChange: v => campoItem('isAliq', v) }}
                        />
                      </tbody>
                    </table>
                  </div>
                  )}
                </Secao>
              )}

              {nfseXml && (() => {
                const lancado = somaValorItens(itens.filter((_, i) => i !== itemEditandoIdx))
                const falta = Math.max(0, Math.round((nfseXml.total - lancado - parseNumBr(itemForm.valorTotal)) * 100) / 100)
                return (
                  <div style={{
                    marginTop: '0.7rem', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 6,
                    padding: '0.5rem 0.8rem', fontSize: '0.78rem', color: '#1a6ea8',
                  }}>
                    📥 NFS-e do XML: total R$ {fmtMoeda(String(nfseXml.total))} · já lançado R$ {fmtMoeda(String(lancado))} · este item R$ {itemForm.valorTotal || '0,00'}
                    {falta > 0
                      ? <> · <b>depois deste ainda faltam R$ {fmtMoeda(String(falta))}</b> (o próximo item abre com esse valor).</>
                      : <> · <b>fecha o total da nota.</b></>}
                    {' '}Pode diminuir o valor pra dividir o serviço em mais itens — o IBS/CBS acompanha proporcional.
                  </div>
                )
              })()}
              {itensImportadosXml && !nfseXml && itemEditandoIdx === null && (
                <div style={{
                  marginTop: '0.7rem', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 6,
                  padding: '0.5rem 0.8rem', fontSize: '0.78rem', color: '#1a6ea8',
                }}>
                  📥 Item {idxItemImportado + 1} de {itensImportadosXml.length} importado do XML — complete Destino/Origem/Natureza e clique em {idxItemImportado + 1 < itensImportadosXml.length ? '"Próximo"' : '"Salvar"'}.
                </div>
              )}
              {erroItem && (
                <div style={{ marginTop: '0.7rem', fontSize: '0.78rem', color: '#dc2626', fontWeight: 600 }}>⚠ {erroItem}</div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.9rem', paddingTop: '0.9rem', borderTop: '1px solid #eef0f4' }}>
                <button className="btn btn-primary btn-sm" disabled={salvandoItem}
                  onClick={() => salvarItem(itemFechaNfse)}
                  style={itemFechaNfse ? { background: '#0f766e', borderColor: '#0f766e' } : undefined}>
                  {salvandoItem ? 'Salvando...'
                    : itemEditandoIdx !== null ? 'Salvar Alterações'
                    : itemFechaNfse ? '✓ Concluir'
                    : nfseXml ? 'Adicionar Item'
                    : itensImportadosXml && idxItemImportado + 1 < itensImportadosXml.length ? 'Próximo →'
                    : itensImportadosXml ? 'Salvar'
                    : 'Adicionar Item'}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setModalItemAberto(false)}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalFinanceiroAberto && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          paddingTop: '2rem', zIndex: 1200,
        }} onClick={() => setModalFinanceiroAberto(false)}>
          <div style={{ background: '#fff', borderRadius: 8, width: 720, maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ background: '#1a6ea8', color: '#fff', padding: '0.9rem 1.25rem', borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: '1rem', fontWeight: 700 }}>Financeiro (Fatura/Duplicatas)</div>
              <button onClick={() => setModalFinanceiroAberto(false)} style={{ background: 'rgba(255,255,255,0.18)', border: 'none', cursor: 'pointer', color: '#fff', borderRadius: 4, padding: '4px 10px', fontSize: '0.9rem' }}>✕</button>
            </div>

            <div style={{ padding: '1rem 1.25rem' }}>
              <Secao titulo="Dados da Fatura">
                <Campo label="Total da Nota" width={130}>
                  <input type="text" value={valorNota} readOnly disabled style={ESTILO_TOTAL} />
                </Campo>
                <Campo label="Condição Pagto." width={260}>
                  <input type="text" value={condicaoPagamentoDes || '(sem condição — parcela única)'} readOnly disabled />
                </Campo>
                <Campo label="Conta Financeira" width={280}>
                  <ContaSelect value={contaFinanceira} onChange={(cod, des) => { setContaFinanceira(cod); setContaFinanceiraDes(des) }} />
                </Campo>
              </Secao>

              <Secao titulo="Parcelas">
                <div style={{ width: '100%', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                      <tr style={{ background: '#1a6ea8' }}>
                        {['Parcela', 'Vencimento', 'Valor', ''].map(h => (
                          <th key={h} style={{ padding: '5px', border: '1px solid #1a6ea8', color: '#fff', fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {parcelas.map((p, idx) => (
                        <tr key={idx}>
                          <td style={{ padding: '3px 5px', border: '1px solid #eef0f4' }}>
                            <input type="text" value={p.numero} maxLength={2} style={{ width: 50, textAlign: 'center' }}
                              onChange={e => alterarParcela(idx, 'numero', e.target.value.replace(/\D/g, ''))} />
                          </td>
                          <td style={{ padding: '3px 5px', border: '1px solid #eef0f4' }}>
                            <input type="date" value={p.vencimento} onChange={e => alterarParcela(idx, 'vencimento', e.target.value)} />
                          </td>
                          <td style={{ padding: '3px 5px', border: '1px solid #eef0f4' }}>
                            <input type="text" inputMode="decimal" value={p.valor} style={{ width: '100%', textAlign: 'right' }}
                              onChange={e => alterarParcela(idx, 'valor', e.target.value)} />
                          </td>
                          <td style={{ padding: '3px 5px', border: '1px solid #eef0f4', textAlign: 'center' }}>
                            <button className="btn-editar" onClick={() => removerParcela(idx)}>Remover</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                  <button className="btn btn-secondary btn-sm" onClick={adicionarParcela}>+ Parcela</button>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: parcelasBatemComTotal ? '#15803d' : '#dc2626' }}>
                    Soma das parcelas: R$ {fmtMoeda(String(totalParcelas))} {parcelasBatemComTotal ? '✓' : `(nota: R$ ${fmtMoeda(String(totalNotaFinanceiro))})`}
                    {totalRetidoNota > 0 && (
                      <div style={{ fontSize: '0.74rem', fontWeight: 400, color: '#6b7280', textAlign: 'right' }}>
                        Nota R$ {fmtMoeda(String(parseNumBr(valorNota)))} − retenções R$ {fmtMoeda(String(totalRetidoNota))}
                      </div>
                    )}
                  </div>
                </div>
              </Secao>

              {erroFinanceiro && (
                <div style={{ marginTop: '0.7rem', fontSize: '0.78rem', color: '#dc2626', fontWeight: 600 }}>⚠ {erroFinanceiro}</div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.9rem', paddingTop: '0.9rem', borderTop: '1px solid #eef0f4' }}>
                <button className="btn btn-primary btn-sm" disabled={salvandoFinanceiro} onClick={salvarFinanceiroClick}>
                  {salvandoFinanceiro ? 'Salvando...' : 'Salvar Financeiro'}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setModalFinanceiroAberto(false)}>
                  Fechar
                </button>
                <button className="btn btn-danger btn-sm" style={{ marginLeft: 'auto' }}
                  disabled={excluindoFinanceiro || salvandoFinanceiro} onClick={excluirFinanceiroClick}>
                  {excluindoFinanceiro ? 'Excluindo...' : 'Excluir Financeiro'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalOSAberto && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300,
        }} onClick={() => setModalOSAberto(false)}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '1.5rem',
            width: 820, maxWidth: '96vw', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.8rem', fontSize: '1rem', fontWeight: 700, color: '#1a6ea8' }}>
              Itens da Ordem de Serviço
            </h3>
            <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'flex-end', marginBottom: '0.8rem', flexWrap: 'wrap' }}>
              <Campo label="Veículo" width={100}>
                <input type="text" value={itemForm.veiculoDes || itemForm.veiculoCod} readOnly disabled />
              </Campo>
              <Campo label="Data Inicial" width={130}>
                <input type="date" value={osDataDe} onChange={e => setOsDataDe(e.target.value)} />
              </Campo>
              <Campo label="Data Final" width={130}>
                <input type="date" value={osDataAte} onChange={e => setOsDataAte(e.target.value)} />
              </Campo>
              <button className="btn btn-secondary btn-sm" onClick={() => buscarOrdensServico(osDataDe, osDataAte)}>
                Buscar
              </button>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', cursor: 'pointer', paddingBottom: 4 }}>
                <input type="checkbox" checked={osTodas}
                  onChange={e => { setOsTodas(e.target.checked); buscarOrdensServico(osDataDe, osDataAte, e.target.checked) }} />
                Mostrar todas (menos canceladas)
              </label>
            </div>
            {carregandoOS &&<div style={{ padding: '1.5rem 0', textAlign: 'center', color: '#6b7280' }}>Carregando...</div>}
            {!carregandoOS && osItens && osItens.length === 0 && (
              <div style={{ padding: '1.5rem 0', textAlign: 'center', color: '#6b7280' }}>
                Nenhum item de OS encontrado para este veículo no período.
              </div>
            )}
            {!carregandoOS && osItens && osItens.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Num. OS</th>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Data</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}>Lin.</th>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Descrição do Serviço</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}>Cód. Srv</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}>Valor</th>
                    {osTodas && <th style={{ padding: '4px 6px', textAlign: 'left' }}>Situação</th>}
                    <th style={{ padding: '4px 6px', width: 90 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {osItens.map((o, idx) => (
                    <tr key={`${o.ordnum}-${o.ordlin}-${idx}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '4px 6px', fontWeight: 700 }}>{o.ordnum}</td>
                      <td style={{ padding: '4px 6px' }}>{fmtDataBr(o.orddat)}</td>
                      <td style={{ padding: '4px 6px', textAlign: 'right' }}>{o.ordlin}</td>
                      <td style={{ padding: '4px 6px' }}>{o.orddes}</td>
                      <td style={{ padding: '4px 6px', textAlign: 'right' }}>{o.ordser_cod}</td>
                      <td style={{ padding: '4px 6px', textAlign: 'right' }}>{fmtMoeda(o.ordvll)}</td>
                      {osTodas && <td style={{ padding: '4px 6px' }}>{({ C: 'Confirmada', E: 'Executada' } as Record<string, string>)[(o.orddmm || '').trim()] ?? (o.orddmm || '').trim()}</td>}
                      <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                        <button onClick={() => selecionarOS(o)}
                          style={{ fontSize: '0.72rem', padding: '3px 8px', borderRadius: 4, border: '1px solid #1a6ea8', background: '#eff6ff', color: '#1a6ea8', cursor: 'pointer', fontWeight: 600 }}>
                          Usar esta
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {modalFinalizarOSAberto && osParaFinalizar && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300,
        }}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '1.5rem',
            width: 420, maxWidth: '96vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 0.4rem', fontSize: '1rem', fontWeight: 700, color: '#1a6ea8' }}>
              OS {osParaFinalizar.ordnum} — Serviço lançado
            </h3>
            <p style={{ margin: '0 0 0.9rem', fontSize: '0.82rem', color: '#374151' }}>
              Anote o número da OS na NF-e impressa. Informe quem executou (Setor) e a data de execução:
            </p>
            <Campo label="Setor" width={260}>
              <LookupSelect
                endpoint="/erp/setores/lista" codKey="setcod" descKey="setdes" tagKey="setcod"
                codValue={setorCod} descValue={setorDes}
                valorExibido={setorCod ? `${setorCod} - ${setorDes}` : ''}
                onSelect={(cod, des) => { setSetorCod(cod); setSetorDes(des) }}
                placeholder="Buscar setor..." listWidth={280}
              />
            </Campo>
            <div style={{ marginTop: '0.6rem' }}>
              <Campo label="Data de Execução" width={160}>
                <input type="date" value={dataExecucaoOS} onChange={e => setDataExecucaoOS(e.target.value)} />
              </Campo>
            </div>
            {erroFinalizacaoOS && (
              <div style={{ marginTop: '0.6rem', fontSize: '0.78rem', color: '#dc2626', fontWeight: 600 }}>⚠ {erroFinalizacaoOS}</div>
            )}
            <p style={{ margin: '0.9rem 0 0.4rem', fontSize: '0.82rem', color: '#374151', fontWeight: 600 }}>
              Quer finalizar a OS agora?
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-primary btn-sm" disabled={salvandoFinalizacaoOS} onClick={() => confirmarFinalizacaoOS(true)}>
                {salvandoFinalizacaoOS ? 'Salvando...' : 'Sim, finalizar'}
              </button>
              <button className="btn btn-secondary btn-sm" disabled={salvandoFinalizacaoOS} onClick={() => confirmarFinalizacaoOS(false)}>
                Não, deixar em aberto
              </button>
            </div>
          </div>
        </div>
      )}

      {modalRecapagem && notaSalva && (
        <ModalRecapagemPneu
          empcod={notaSalva.empcod} serie={notaSalva.serie} numero={notaSalva.numero}
          key={modalRecapagem.pnvLin}
          pnvLin={modalRecapagem.pnvLin} pnucod={modalRecapagem.pnucod}
          procedimento={modalRecapagem.procedimento} dataEmissao={dataEmissao}
          valorInicial={modalRecapagem.valorInicial}
          fornecedorNotaCod={fornecedorCod} fornecedorNotaDes={fornecedorDes}
          onClose={() => { proximaRecapagem(); setConcluirAgora(false) }}
          onSalvo={proximaRecapagem}
        />
      )}
    </div>
  )
}
